<template>
  <div
    class="card-editor user-select-none"
    v-bind:class="{
      'drop-zone-background': canDrag && isDragging,
      'preview': isPreview,
      'disable-zoom': [ 'edit', 'purcase', 'design' ].includes(mode)
    }"
    v-bind:style="{ 'padding-top': paddingTop + 'px' }"
    @dragover.prevent="registerDrag"
    @touchmove="onGlobalTouchMove"
  >

    <dialog-scroll-lock v-if="selected?.text?.event_link && (card.type == 'INVITATION')" width="300" v-model="showSmartText">
      <v-card>
        <v-btn class="float-right mt-3 mr-3" icon>
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>Smart text</v-card-title>
        <v-card-subtitle class="pb-3">
          This text can auto-format based on your <a @click="$emit('step-to', 'event')">event's {{eventLinkFieldsReadable}}<v-icon x-small v-html="mdiChevronRight" /></a>
        </v-card-subtitle>
        <v-list class="primary--text" dense>
          <v-list-item class="simple" @click.capture.stop.prevent="updateTextEventLink(false)">
            <v-icon v-html="selected.text.event_link.disabled ? mdiRadioboxBlank : mdiRadioboxMarked" />
            <div>Auto-format</div>
          </v-list-item>
          <v-list-item class="simple" @click.capture.stop.prevent="updateTextEventLink(true)">
            <v-icon v-html="selected.text.event_link.disabled ? mdiRadioboxMarked : mdiRadioboxBlank" />
            <div>
              <div>Edit text directly</div>
              <div class="text--label-active">If you change your event, make sure you also update this text</div>
            </div>
          </v-list-item>
        </v-list>
      </v-card>
    </dialog-scroll-lock>

    <div v-if="card.sample || (card.api == 'TEST')" class="absolute-fill watermark" />

    <template v-if="![ 'view', 'preview' ].includes(mode)">

      <snackbar persistent v-model="uploadImageStatus.pending || uploadVideoStatus.pending">
        <v-progress-circular
          class="mr-2" width="2" size="18"
          :indeterminate="!transcodeVideoStatus.pending"
          :value="transcodeVideoStatus.pending ? transcodeT : null"
        />
        <span v-if="transcodeVideoStatus.pending">Converting file, please wait…</span>
        <span v-else>Processing file, please wait…</span>
      </snackbar>

      <error-dialog :status="uploadImageStatus" />
      <error-dialog :status="uploadVideoStatus" />
      <error-dialog :status="onDropStatus" />

      <replace-design-dialog
        v-if="showReplaceDesign"
        v-model="showReplaceDesign"
        :design-id="card.design_id"
        @select="x => $emit('replace-design', x)"
      />

      <link-btn-dialog
        v-if="showEditBtn"
        :btn="showEditBtn"
        @input="x => {
          if (selected)
            Object.assign(selected.btn, x);
          else
            insertBtn(x);
          showEditBtn = null;
          $emit('save');
        }"
        @close="showEditBtn = null"
      />

      <tenor-dialog v-if="showGiphy" v-model="showGiphy" @select="insertFromGiphy" />

      <giftcard-dialog
        v-if="showGiftcard"
        v-model="showGiftcard"
        :order-id="orderId"
        :order="card"
        :selected-page-id="selectedPage.id"
        :is-completed="card.state == 'COMPLETED'"
        :max-value="maxGiftcardValue"
        @select="insertGiftcard"
        @reload="$emit('reload')"
      />

      <record-video-dialog
        v-if="showRecord"
        :has-audio="true"
        :max-duration="60"
        :aspect-ratio="selected?.mask ? (selected.mask.w / selected.mask.h) : null"
        @input="onRecordInput"
      />

      <!-- Hidden inputs to accept image and video uploads -->
      <!-- NOTE: I don't quite understand why, but the image/heic type has to be explicit -->
      <input v-show="false" ref="upload-image-input" type="file" accept="image/*,image/heic" @click.stop />
      <input v-show="false" ref="upload-video-input" type="file" accept="video/*,video/3gpp" @click.stop />
    </template>

    <v-menu
      v-if="contextMenu && selected && !isPageReadonly"
      offset-y max-width="320"
      v-bind="contextMenu.activator ? {
        'top': true,
        'nudge-left': 80,
        'nudge-top': 2,
        'activator': contextMenu.activator
      } : {
        'absolute': true,
        'position-x': contextMenu.x,
        'position-y': contextMenu.y
      }"
      :close-on-content-click="false"
      :value="true"
      @input="contextMenu = null"
    >
      <v-list dense>
        <div class="mt-n2 d-flex align-end">
          <v-list-item @click="selected = null">
            <v-list-item-icon class="mr-3">
              <v-icon v-html="mdiSelection" />
            </v-list-item-icon>
            <v-list-item-title>Deselect</v-list-item-title>
          </v-list-item>
          <card-preview
            class="mt-2 mx-2 v-sheet elevation-1 preview-selected"
            v-bind:class="{ 'selected-owner': isPrimary(selected) }"
            style="width: 48px; background-color: #FFF8;"
            :mode="mode"
            :is-card-owner="isOwner"
            :card="{
              ...card,
              background: {},
              pages: [
                {
                  id: 'selected-blob',
                  blobs: [ selected ]
                }
              ]
            }"
          />
        </div>
        <template v-if="(card.user_id == userId) && !isPrimary(selected) ">
          <v-divider class="my-2" />
          <div class="my-n2 py-2 px-3 text--label-active grey lighten-4">
            <div>Created by</div>
            <div class="font-italic" style="min-height: 15px;">
              <template v-if="loadSelectedSignatureStatus.pending">…</template>
              <template v-else>{{selectedSignature.email | email}}</template>
            </div>
          </div>
        </template>
        <v-divider class="my-2" />
        <design-blob-list-options
          v-if="mode == 'design'"
          :blob="selected" :design="card" :can-transform="canTransformSelected"
          @set-static-img="$emit('set-static-img', selected)"
        />
        <template v-if="showAutomationOpts && selected.img">
          <v-menu
            right offset-x max-width="300"
            @input="
              v => {
                if (v) {
                  selectedPersonalizationKey = /^{[^{}]+}$/.test(selected.img.src) ? selected.img.src.slice(1, -1).trim() : '';
                  selectedPersonalizationEnabled = !!selectedPersonalizationKey;
                }
              }
            "
          >
            <template v-slot:activator="{ on }">
              <v-list-item
                v-on="on" v-blur
                :color="/^{[^{}]+}$/.test(selected.img.src) ? 'primary' : null"
                :input-value="/^{[^{}]+}$/.test(selected.img.src)"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon v-html="mdiImageMultiple" />
                </v-list-item-icon>
                <v-list-item-title>Image personalization</v-list-item-title>
                <v-list-item-icon>
                  <v-icon v-html="mdiChevronRight" />
                </v-list-item-icon>
              </v-list-item>
            </template>
            <div class="white pa-3" @click.stop>
              <v-checkbox
                class="pt-0 pb-3"
                label="Enable image personalization" dense hide-details
                v-model="selectedPersonalizationEnabled"
                @change="
                  v => {
                    if (v) {
                      selectedPersonalizationKey = 'image url';
                      selected.img.src = '{ image url }';
                    } else {
                      selected.img.src = '/template-placeholder.png';
                    }
                  }
                "
              />
              <v-expand-transition>
                <v-text-field
                  v-if="selectedPersonalizationEnabled"
                  class="ml-8"
                  label="Image URL column name" dense outlined hide-details persistent-placeholder
                  :error="!selectedPersonalizationKey || /[{}]/.test(selectedPersonalizationKey)"
                  v-model="selectedPersonalizationKey"
                  @input="v => selected.img.src = (!v || /[{}]/.test(v)) ? '/template-placeholder.png' : `{ ${v} }`"
                />
              </v-expand-transition>
              <div class="pt-3 text--label-active">
                Use this option to send a unique image to each recipient of an automation. You'll provide a URL that points to the target image for each recipient in the <i>Delivery schedule</i> step.<br><br>
                <v-icon x-small v-html="mdiAlert" />
                Make sure all your images support <a target="_blank" href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/CORS">cross-origin loading</a> and are the same size so they don't get distorted.
              </div>
            </div>
          </v-menu>
          <v-divider class="my-2" />
        </template>
        <template
          v-for="[ icon, title, isDisabled, action ] in [
            [
              mdiArrangeBringForward,
              'Move forward',
              () => ((mode != 'design') && !!selected.template) || (selectedI == selectedPage.blobs.length - 1),
              () => updateSelectedBlobLayer(1)
            ],
            [
              mdiArrangeSendBackward,
              'Move backward',
              () => !selectedI || ((mode != 'design') && (!!selected.template || !!selectedPage.blobs[ selectedI - 1 ].template)),
              () => updateSelectedBlobLayer(-1)
            ],
            [ mdiLayers, 'Show layers', () => false, () => viewAs = 'layers' ],
            [],
            [
              mdiContentCopy,
              'Duplicate',
              () => ![ 'TEXT', 'IMG', 'VIDEO' ].includes(selected.type) || (selected.mask && (selected.mask.placeholder == selected.img?.src)) || ((selected.type == 'TEXT') && !selected.text.text),
              duplicateSelectedBlob
            ],
            [],
            [
              mdiChevronLeft,
              'Move to previous page',
              () => !!selected.template || !selectedPageI || ((selected.type == 'TEXT') && !selected.text.text),
              () => updateSelectedBlobPage(-1)
            ],
            (selectedPageI == card.pages.length - 1) ? [
              iconPathChevronRightPlus,
              'Move to new page',
              () => !!selected.template || ((selected.type == 'TEXT') && !selected.text.text),
              () => updateSelectedBlobPage(1)
            ] : [
              mdiChevronRight,
              'Move to next page',
              () => !!selected.template || ((selected.type == 'TEXT') && !selected.text.text),
              () => updateSelectedBlobPage(1)
            ],
            [],
            selected.mask ? [
              mdiReload,
              'Reset',
              () => selected.mask.placeholder == selected.img?.src,
              () => {
                selected.x = selected.mask.x;
                selected.y = selected.mask.y;
                selected.w = selected.mask.w;
                selected.h = selected.mask.h;
                selected.r = selected.mask.r;
                selected.img.src = selected.mask.placeholder;
              }
            ] : [
              mdiDelete,
              'Delete',
              () => (selected.type == 'TEXT') && !selected.text.text,
              () => {
                if (selected.template)
                  selected.text.text = '';
                else
                  tryDeleteBlob(selectedPageI, selectedI);
              }
            ]
          ]"
        >
          <v-list-item v-if="title" :disabled="isDisabled()" @click="action">
            <v-list-item-icon class="mr-3">
              <v-icon v-html="icon" />
            </v-list-item-icon>
            <v-list-item-title v-text="title" />
          </v-list-item>
          <v-divider v-else class="my-2" />
        </template>
      </v-list>
    </v-menu>

    <div
      v-if="viewAs == 'grid'"
      ref="draggable"
      class="absolute-fill overflow-auto"
      style="padding-bottom: 80px; z-index: 1;"
      v-bind:style="{ 'padding-top': gridPadding + 'px' }"
    >
      <!-- Note that that there is a bug with the 'move' check - the draggedContext.futureIndex does not get set correctly when dragging an item in between rows -->
      <draggable
        class="ma-0 pa-3"
        style="
          display: grid;
          justify-content: space-between;
          pointer-events: auto;
          width: 100%;
        "
        v-bind:style="{
          'grid-template-columns': `repeat(auto-fill, ${$vuetify.breakpoint.xsOnly ? '33%' : 'minmax(200px, 1fr)'})`
        }"
        draggable=".draggable"
        handle=".page-grid-draggable-handle"
        ghost-class="draggable-ghost"
        :disabled="!isOwner || [ 'view', 'preview' ].includes(mode)"
        :value="pages"
        :move="e => !e.related.classList.contains('draggable-disabled')"
        @start="e => $refs.draggable && $refs.draggable.classList.add('is-dragging')"
        @end="e => {
          $refs.draggable && $refs.draggable.classList.remove('is-dragging');
          swapDesignBlobs(card.pages[e.oldIndex], card.pages[e.newIndex]);
          if (e.oldIndex == selectedPageI)
            selectedPageI = e.newIndex;
          $emit('input', card);
          $emit('save');
        }"
        @input="v => card.pages = fixedLastPage ? v.slice(0, -1) : v"
      >
        <div
          v-for="page, i in pages"
          ref="grid-page"
          class="pa-3 relative draggable"
          v-bind:class="{
            'draggable-disabled': (mode != 'design') && ((i <= lastDesignPageI) || (fixedLastPage && (i >= card.pages.length))),
            'is-selected-page': i == selectedPageI
          }"
        >
          <v-btn
            v-if="(mode == 'design') || (isOwner && ![ 'view', 'preview' ].includes(mode) && (i > lastDesignPageI) && (!fixedLastPage || (i < card.pages.length)))"
            class="absolute page-grid-draggable-handle"
            style="bottom: -8px; left: calc(50% - 28px); z-index: 1; cursor: grab;"
            fab color="primary"
            :disabled="card.pages.length == 1"
          >
            <v-icon large v-html="mdiArrowAll" />
            <span class="btn-tooltip top" v-bind:class="{ persistent: showHelp && (i == lastDesignPageI + 1) }">
              Drag to rearrange
            </span>
          </v-btn>
          <v-card class="overflow-hidden" hover tile @click="closeGrid(i)">
            <v-responsive :aspect-ratio="5 / 7">
              <v-lazy>
                <card-preview :card="{ ...card, pages: [ page ] }" :mode="mode" :is-card-owner="isOwner" />
              </v-lazy>
            </v-responsive>
          </v-card>
        </div>
      </draggable>
    </div>

    <div
      v-else-if="viewAs == 'layers'"
      class="absolute-fill d-flex flex-column justify-center align-center"
      style="z-index: 1;"
    >
      <v-spacer />
      <draggable
        class="fill-width overflow-auto py-3"
        draggable=".draggable"
        handle=".layer-draggable-handle"
        ghost-class="draggable-ghost"
        v-model="selectedPageLayers"
        :move="e => !e.related.classList.contains('draggable-disabled')"
        @input="updateLayers"
      >
        <div
          v-for="layer, i in selectedPageLayers" :key="'layer-' + i"
          v-if="layer.visible"
          class="draggable d-flex align-center relative px-3"
          v-bind:class="{ 'draggable-disabled': !layer.rearrange }"
          style="width: max(50%, 340px); max-width: 480px; margin: auto;"
          v-bind:style="{ 'z-index': selectedPageLayers.length - i }"
        >
          <div class="btn-group">
            <v-btn v-blur class="btn-group-right layer-draggable-handle" fab :disabled="!layer.rearrange">
              <v-icon v-html="layer.rearrange ? mdiArrowSplitHorizontal : mdiLock" />
            </v-btn>
            <v-btn
              v-blur
              class="btn-group-left"
              fab
              :disabled="!layer.rearrange || (layer.blobs.length != 1)"
              @click="
                $set(layer.blobs[0], 'opacity', isTransparent(layer.blobs[0]) ? 1 : 0);
                $emit('update-blob', selectedPage.blobs.findIndex(x => x.id == layer.blobs[0].id), layer.blobs[0]);
                $emit('input', card);
              "
            >
              <v-icon
                :color="layer.blobs[0] && isTransparent(layer.blobs[0]) ? 'warning' : null"
                v-html="(!layer.blobs[0] || !isTransparent(layer.blobs[0])) ? mdiEye : mdiEyeOff"
              />
            </v-btn>
          </div>
          <div class="flex-grow-1">
            <v-card
              style="width: 25%; transform: scale(2.2) rotateX(60deg) skew(-15deg, 0)"
              class="mx-auto"
              v-bind:class="{ 'layer-hidden': layer.blobs[0] && isTransparent(layer.blobs[0]) }"
              hover tile color="#FFF8" width="100%"
              @click="selectLayer(layer)"
            >
              <card-preview
                :mode="mode"
                :is-card-owner="isOwner"
                :card="{
                  ...card,
                  background: i == selectedPageLayers.length - 1 ? card.background : {},
                  pages: [
                    {
                      id: 'page-' + (layer.blobs.length ? layer.blobs[0].id : 'bg-' + i),
                      blobs: [
                        ...layer.blobs.filter(x => x.mask).map(x => ({ id: x.id + '-mask', type: 'IMG', ...x.mask, img: { src: '/template-mask-placeholder.png', frame: null } })),
                        ...layer.blobs.map(x => ({ ...x, mask: undefined, template: undefined, opacity: 1 }))
                      ]
                    }
                  ]
                }"
              />
            </v-card>
          </div>
        </div>
        <div style="min-height: 80px;" />
      </draggable>
      <v-spacer />
    </div>

    <div
      ref="pages-container"
      class="pa-3 d-flex align-center relative"
      style="height: calc(100% - 68px);"
      @touchstart.capture="onGestureStart"
      @touchmove.capture="onGestureMove"
      @touchend.capture="onGestureEnd"
      v-touch="{
        right: () => !isTransformInProgress && !isZoomed() && swipe(-1),
        left: () => !isTransformInProgress && !isZoomed() && swipe(1)
      }"
      @contextmenu="e => !e.shiftKey && e.preventDefault()"
    >

      <!-- You can use the following event to do something when the page turn animation completes: @transitionend="onPageTurn" -->
      <div
        v-for="(page, i) in pages"
        v-if="!viewAs && (Math.abs(selectedPageI - i) < 5) && hasPageRect && (i == 0 || (!isPreview && !coverOnly))"
        :key="'pg-' + i"
        class="page-transition-container"
        v-bind:class="{ transition: !isPreview && !disablePageTransition }"
        v-bind:style="{
          'left': `calc(50% + ${ getPageOffset(i) }px)`,
          'z-index': pages.length - Math.abs(i - selectedPageI),
          'transform': 'scale(' + getScale(i) + ')',
          'opacity': (Math.abs(selectedPageI - i) >= 4) ? .25 : (Math.abs(selectedPageI - i) == 3) ? .5 : 1
        }"
        @dragenter="onDragEnter(i)"
        @dragover="dragOverPage = i"
        @drop.prevent="e => canDrag && onDrop(i, e)"
      >

        <div
          v-if="(selectedPageI == i) && canDrag && isDragging && !hasDropZones(page)"
          class="d-flex justify-center align-center drop-zone absolute-fill"
          v-bind:class="{ 'drop-zone-readonly': isPageReadonly }"
        >
          <v-btn icon x-large :color="isPageReadonly ? 'warning' : 'white'">
            <v-icon x-large v-html="isPageReadonly ? mdiUploadOff : mdiUpload" />
          </v-btn>
        </div>

        <template v-if="selected && (blobTransformsInProgress[selected.id] == 'move') && changeBlobPageD">
          <div
            v-if="(i == selectedPageI + changeBlobPageD) && (!fixedLastPage || (i < card.pages.length))"
            class="d-flex justify-center align-center drop-zone absolute-fill"
          />
          <div
            v-else-if="(i == selectedPageI) && (changeBlobPageD == 1) && (i == card.pages.length - 1)"
            class="v-tooltip__content tooltip-btn menuable__content__active"
            style="z-index: 1; top: 12px; right: 12px;"
          >
            Create new page<v-icon class="ml-1 mr-n1" color="white" v-html="iconPathChevronRightPlus" />
          </div>
        </template>

        <div
          :ref="selectedPageI == i ? 'page-container' : null"
          class="page-container"
          v-bind:style="{
            transform: 'scale(' + pageRect.scale + ')',
            width: pageRect.w + 'px',
            height: pageRect.h + 'px'
          }"
        >
          <!-- Note that we use 'mousedown' instead of a simple 'click' - this is because a 'click' fires where it is released, and we do not want a click that starts in a blob then travels to the page to trigger anything (common when rotating a blob if your finger moves outside the button) -->
          <v-sheet
            elevation="8"
            class="page-sheet"
            v-bind:class="{
              'will-hide': willHide(i),
              wobble: !didInteract && (i == 0) && (selectedPageI == 0) && (pages.length > 1) && $vuetify.breakpoint.xsOnly && (mode == 'view')
            }"
            v-bind:style="{
              width: W + 'px',
              height: H + 'px',
              cursor: !selected && (selectedPageI == i) && !isPageReadonly ? 'cell' : 'inherit',
              'background-image': card.background.img ? `url('${card.background.img}')` : null,
              'background-color': card.background.color
            }"
            @click.native.stop="e => onPageClick(e, i)"
          >

            <template v-if="(selectedPageI == i) && snapToGuides">
              <div
                v-if="guide.horizontal != -1"
                class="absolute fill-width"
                style="z-index: 2; border: 1px dashed #1976d288;"
                v-bind:style="{ top: `min(calc(100% - 2px), ${100 * guide.horizontal}%)` }"
              />
              <div
                v-if="guide.vertical != -1"
                class="absolute fill-height"
                style="z-index: 2; border: 1px dashed #1976d288;"
                v-bind:style="{ left: `min(calc(100% - 2px), ${100 * guide.vertical}%)` }"
              />
            </template>

            <blob
              v-for="(blob, j) in page.blobs" :key="blob.id" :ref="blob.id"
              v-if="isBlobVisible(blob)"
              :blob="blob"
              :user-id="userId"
              :skin="card.skin"
              :is-cropping="isCropping"
              :is-page-active="(selectedPageI == i) && !isSwiping"
              :is-page-readonly="isPageReadonly"
              :is-above-selected="j > selectedI"
              :is-primary="isPrimary(blob)"
              :is-card-owner="isOwner"
              :is-verified-recipient="isVerifiedRecipient"
              :mode="mode"
              :scale="pageRect.scale"
              :selected="selected"
              :is-root-transform-in-progress="isRootTransformInProgress"
              :is-dragging-on-page="isDragging || ((selected?.type == 'IMG' || selected?.type == 'VIDEO') && (blobTransformsInProgress[selected.id] == 'move'))"
              :transform-in-progress="blobTransformsInProgress[blob.id]"
              :snap="snapToGuides"
              @update:transform-in-progress="v => {
                if (v)
                  $set(blobTransformsInProgress, blob.id, v)
                else
                  $delete(blobTransformsInProgress, blob.id);
              }"
              @select="() => { if (!gestureStart) selected = blob; }"
              @deselect="selected = null"
              @delete="immediate => immediate ? deleteBlob(i, j) : tryDeleteBlob(i, j)"
              @input="x => { fixScroll(); $emit('update-blob', i, x); $emit('input', card); }"
              @loaded="i == 0 ? loadedCoverBlob(blob.id) : null"
              @prompt-upload="() => {
                if (blob.type == 'IMG')
                  promptUpload('image', f => uploadImage('selected-template', f));
                else
                  promptUpload('video', f => uploadVideo('selected-template', f));
              }"
              @prompt-record-video="showRecord = true"
              @click-btn="$emit('click-btn', blob)"
              @click-giftcard="$emit('click-giftcard', blob)"
              @click-group-video="$emit('click-group-video', blob)"
              @click-text-event-link="() => {
                selected = blob;
                if (card.type == 'INVITATION')
                  $nextTick(() => showSmartText = true);
                else
                  updateTextEventLink(true);
              }"
              @move-end="onMoveEnd"
              @play-video="$emit('play-video')"
              @guide="(axis, pct) => guide[axis] = pct"
              @contextmenu.native="e => !e.shiftKey && (selected === blob) && onBlobContextMenu(e)"
            />
          </v-sheet>
        </div>
      </div>
    </div>

    <!-- Button controls -->
    <div v-if="!coverOnly" class="absolute-fill pa-3" v-bind:style="{ top: (paddingTop + gridPadding) + 'px' }">
      <div
        v-if="!isPreview"
        class="btn-container d-flex flex-column"
        v-bind:style="{ 'z-index': pages.length + 1 }"
      >

        <div
          class="main-btns flex-grow-1 d-flex"
          v-bind:class="{ show: !viewAs && (showButtons || $vuetify.breakpoint.mdAndUp) }"
        >

          <!-- Top-left column: blob type-specific settings -->
          <div class="d-flex flex-column btn-container-left" style="width: 64px;">

            <!-- Controls for specific blob types -->

            <template v-if="selected && (isPrimary(selected) || isOwner) && selected.type == 'TEXT'">
              <v-btn
                v-if="!selected.template || mode == 'design'"
                v-blur
                class="btn-top"
                fab dark color="deep-orange lighten-1"
                @click.stop="toggleFitToPage"
              >
                <v-icon v-html="selected.fixed_position ? iconPathUnfitToPage : iconPathFitToPage" />
                <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">
                  {{selected.fixed_position ? 'Shrink from page' : 'Fit to page'}}
                </span>
              </v-btn>

              <!-- Note: anywhere you've got tooltips and menus on the same component, make sure the tooltip is outside the menu or else you'll see some weird layout issues -->

              <font-picker-btn :fonts="fonts" :right="true" v-model="selected.text.font">
                <template v-slot="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="orange" class="btn-top">
                    <!-- iconPathFont -->
                    <v-icon v-html="mdiFormatFont" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Font</span>
                  </v-btn>
                </template>
              </font-picker-btn>

              <color-picker-btn :colors="colors" v-model="selected.text.color">
                <template v-slot="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="amber" class="btn-top">
                    <v-icon v-html="mdiPalette" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Color</span>
                  </v-btn>
                </template>
              </color-picker-btn>

              <v-menu content-class="overflow-visible" offset-x nudge-right="4" min-width="224">
                <template v-slot:activator="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="purple lighten-3" class="btn-top">
                    <v-icon v-html="mdiTextShadow" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Effects</span>
                  </v-btn>
                </template>
                <div class="white d-flex flex-wrap btn-group" style="width: 225px;" @click.stop>
                  <v-btn
                    v-for="effect, i in TEXT_EFFECTS" :key="'ef-' + effect.name"
                    class="text-effect-btn text-center d-flex flex-column justify-center overflow-hidden"
                    :dark="isSelectedLightColor"
                    :input-value="isEffectActive(effect)"
                    @click="toggleEffect(effect)"
                  >
                    <div
                      v-bind:style="{ color: selected.text.color, ...effect.css, ...effect.cssPreview }"
                      style="font-size: 2.5em;"
                    >
                      Aa
                    </div>
                    <div style="font-size: 12px;">{{effect.name}}</div>
                  </v-btn>
                </div>
              </v-menu>

              <frame-btn :options="selectedOptions" :show-background="false">
                <template v-slot="{ on }">
                  <v-btn
                    v-blur v-on="on"
                    class="btn-top"
                    fab dark color="brown lighten-1"
                    :disabled="!!selected.fixed_position"
                  >
                    <v-icon v-html="iconPathFrame" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Frame</span>
                  </v-btn>
                </template>
              </frame-btn>

              <v-menu
                content-class="overflow-visible" offset-x nudge-right="4" min-width="200"
                :close-on-content-click="false"
                v-model="fontAlignmentMenu"
              >
                <template v-slot:activator="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="pink lighten-2" class="btn-top">
                    <v-icon v-html="mdiFormatAlignTop" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Alignment / sizing</span>
                  </v-btn>
                </template>
                <div class="white pa-3" @click.stop>
                  <div class="my-1 relative">
                    <div class="text--label-active">Text alignment</div>
                    <v-btn
                      class="font-align-btn"
                      small icon
                      :color="(selectedOptions.align == 'justify') ? 'primary' : null"
                      :input-value="selectedOptions.align == 'justify'"
                      @click="selectedOptions.align = (selectedOptions.align == 'justify') ? 'center' : 'justify'"
                    >
                      <v-icon small v-html="mdiFormatAlignJustify" />
                    </v-btn>
                  </div>
                  <div class="mx-n2" style="display: grid; grid-template-columns: repeat(3, 1fr);">
                    <div
                      v-for="
                        [ r, align, valign ] in [
                          [ -45, 'left', 'start' ],
                          [ 0, 'center', 'start' ],
                          [ 45, 'right', 'start' ],
                          [ -90, 'left', 'center' ],
                          [ null, 'center', 'center' ],
                          [ 90, 'right', 'center' ],
                          [ -135, 'left', 'end' ],
                          [ 180, 'center', 'end' ],
                          [ 135, 'right', 'end' ]
                        ]
                      "
                      :class="`text-${align}`"
                    >
                      <v-btn
                        style="min-width: unset; padding: 6px;"
                        text rounded color="primary"
                        :disabled="(selectedOptions.align == 'justify') && (align != 'center')"
                        :input-value="((align == selectedOptions.align) || ((selectedOptions.align == 'justify') && align == 'center')) && (valign == selectedOptions.valign)"
                        @click="Object.assign(selectedOptions, { align: (selectedOptions.align == 'justify') ? 'justify' : align, valign })"
                      >
                        <v-icon
                          v-if="(align == 'center') && (selectedOptions.align == 'justify')"
                          class="mx-1"
                          small
                          v-html="mdiFormatAlignJustify"
                        />
                        <v-icon
                          v-bind:style="{ transform: `rotate(${r}deg)` }"
                          v-html="(r == null) ? mdiArrowAll : mdiArrowUpBold"
                        />
                      </v-btn>
                    </div>
                  </div>

                  <div class="mt-6 mb-1 relative">
                    <div class="text--label-active">Line height</div>
                    <v-btn
                      v-if="((selected.text.css || {})['line-height'] || 1.5) != selectedTextCSSDefaults['line-height']"v-blur
                      class="font-align-btn"
                      small icon
                      @click="$set(selected.text, 'css', { ...(selected.text.css || {}), 'line-height': selectedTextCSSDefaults['line-height'] })">
                      <v-icon small v-html="mdiReload" />
                    </v-btn>
                  </div>
                  <v-slider
                    hide-details min="0.8" max="2.2" step=".05"
                    :value="(selected.text.css || {})['line-height'] || 1.5"
                    @input="v => $set(selected.text, 'css', { ...(selected.text.css || {}), 'line-height': v })"
                  />
                  <div class="mt-6 mb-1 relative">
                    <div class="text--label-active">Letter spacing</div>
                    <v-btn
                      v-if="((selected.text.css || {})['letter-spacing'] || '0em') != selectedTextCSSDefaults['letter-spacing']"
                      v-blur
                      class="font-align-btn"
                      small icon
                      @click="$set(selected.text, 'css', { ...(selected.text.css || {}), 'letter-spacing': selectedTextCSSDefaults['letter-spacing'] })">
                      <v-icon small v-html="mdiReload" />
                    </v-btn>
                  </div>
                  <v-slider
                    hide-details min="-.1" max=".2" step=".01"
                    :value="((selected.text.css || {})['letter-spacing'] || '0em').slice(0, -2)"
                    @input="v => $set(selected.text, 'css', { ...(selected.text.css || {}), 'letter-spacing': v + 'em' })"
                  />
                  <div class="mt-6 mb-1 relative">
                    <div class="text--label-active">Maximum font size</div>
                    <v-btn v-blur class="font-align-btn" small icon @click="showFontSizeSlider = !showFontSizeSlider">
                      <v-icon small v-html="showFontSizeSlider ? mdiCounter : mdiToggleSwitchOff" />
                    </v-btn>
                  </div>
                  <!-- Use a non-linear slider since fine-grained control is more important at smaller numbers -->
                  <!-- <v-slider hide-details min="18" max="480" step="6" v-model="selected.text.size" /> -->
                  <v-slider
                    v-if="showFontSizeSlider"
                    hide-details min="0" max="21.5" step=".25"
                    :value="Math.sqrt(selected.text.size - 18)"
                    @input="v => selected.text.size = Math.round(v * v + 18)"
                  />
                  <int-field v-else class="mt-1" required :min="18" :max="480" v-model="selected.text.size" />
                </div>
              </v-menu>
            </template>

            <template v-if="selected && (isPrimary(selected) || isOwner) && [ 'IMG', 'VIDEO', 'GROUP_VIDEO' ].includes(selected.type) && !selected.template">

              <v-btn
                v-blur
                fab dark color="deep-orange lighten-1"
                class="btn-top"
                :disabled="isCropping"
                @click.stop="toggleFitToPage"
              >
                <v-icon v-html="selected.fixed_position ? iconPathUnfitToPage : iconPathFitToPage" />
                <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">
                  {{selected.fixed_position ? 'Shrink from page' : 'Fit to page'}}
                </span>
              </v-btn>

              <v-btn
                v-if="selected.type != 'GROUP_VIDEO'"
                v-blur
                fab dark color="amber accent-4"
                class="btn-top"
                v-bind:class="{ pulse: isCropping }"
                :disabled="selected.fixed_position"
                @click.stop="toggleCrop"
              >
                <v-icon v-html="mdiCrop" />
                <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">
                  {{isCropping ? 'Exit crop tool' : 'Crop'}}
                </span>
                <div
                  v-if="isCropping && !didHideCropTip"
                  class="btn-tooltip btn-tooltip-prominent right elevation-20 persistent pa-0"
                  style="width: 200px; text-wrap: wrap; z-index: 100;"
                  @click.stop="didHideCropTip = true"
                >
                  <div class="pa-3">Click here when you're finished with the crop tool</div>
                  <v-divider />
                  <v-btn class="fill-width" text dark>Got it</v-btn>
                </div>
              </v-btn>

              <frame-btn :options="selectedOptions" :show-background="true">
                <template v-slot="{ on }">
                  <v-btn
                    v-blur v-on="on"
                    fab dark color="brown lighten-1"
                    class="btn-top"
                    :disabled="!!selected.fixed_position || isCropping"
                  >
                    <v-icon v-html="iconPathFrame" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Frame</span>
                  </v-btn>
                </template>
              </frame-btn>

            </template>

            <template v-if="selected && (isPrimary(selected) || isOwner) && selected.type == 'BTN'">
              <v-btn
                v-blur
                class="btn-top"
                fab dark color="orange darken-2"
                @click.stop="() => {
                  if (selected.btn.form)
                    $emit('click-edit-form');
                  else
                   showEditBtn = selected.btn;
                }"
              >
                <v-icon v-html="mdiPencil" />
                <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">
                  Edit {{selected.btn.form ? 'event details' : 'link'}}
                </span>
              </v-btn>

              <color-picker-btn :colors="[ ...colors, '#0000' ]" v-model="selected.btn.color">
                <template v-slot="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="amber" class="btn-top">
                    <v-icon v-html="mdiPalette" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Button color</span>
                  </v-btn>
                </template>
              </color-picker-btn>

              <v-menu offset-x nudge-right="4" min-width="224">
                <template v-slot:activator="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="pink lighten-3" class="btn-top">
                    <v-icon v-html="mdiFormatSize" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Button size</span>
                  </v-btn>
                </template>
                <div class="white pa-3" @click.stop>
                  <v-slider
                    hide-details
                    min="1" max="3" step=".25"
                    :value="selected.btn.scale || 1"
                    @start="isRootTransformInProgress = true"
                    @end="isRootTransformInProgress = false"
                    @input="v => selected.btn.scale = v"
                  />
                </div>
              </v-menu>
            </template>

            <template v-if="selected && (isPrimary(selected) || isOwner) && selected.type == 'GRADIENT'">
              <color-picker-btn
                :colors="[ '#FFFFFFFF', '#7F7F7FFF', '#000000FF', '#FFFFFF00', '#7F7F7F00', '#00000000' ]"
                v-model="selected.gradient.start"
              >
                <template v-slot="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="amber" class="btn-top">
                    <v-icon v-html="mdiPalette" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Start color</span>
                  </v-btn>
                </template>
              </color-picker-btn>
              <color-picker-btn
                :colors="[ '#FFFFFFFF', '#7F7F7FFF', '#000000FF', '#FFFFFF00', '#7F7F7F00', '#00000000' ]"
                v-model="selected.gradient.stop"
              >
                <template v-slot="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="orange" class="btn-top">
                    <v-icon v-html="mdiPalette" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Stop color</span>
                  </v-btn>
                </template>
              </color-picker-btn>
              <v-menu offset-x nudge-right="4" min-width="224">
                <template v-slot:activator="{ on }">
                  <v-btn v-blur v-on="on" fab dark color="brown" class="btn-top">
                    <v-icon v-bind:style="{ transform: `rotate(${selected.gradient.r}deg)` }" v-html="mdiArrowUpThin" />
                    <span class="btn-tooltip right" v-bind:class="{ persistent: showHelp }">Direction</span>
                  </v-btn>
                </template>
                <div class="white pa-3" @click.stop>
                  <v-slider
                    hide-details
                    min="0" max="360"
                    :step="snapToGuides ? 45 : 1"
                    v-model="selected.gradient.r"
                    @start="isRootTransformInProgress = true"
                    @end="isRootTransformInProgress = false"
                  />
                </div>
              </v-menu>
            </template>

          </div>

          <!-- Top-center (you need this to act as a spacer) -->
          <div class="flex-grow-1 d-flex justify-center">
            <!--
            <tooltip-btn
              v-if="mode != 'design'"
              v-slot="{ on: tooltip, bind }"
              key="add-gift-tooltip"
              msg="Add gift" left
              :disabled="!!selected"
              :persistent="showHelp"
            >
              <v-menu offset-y bottom>
                <template v-slot:activator="{ on: menu }">
                  <v-btn v-blur v-on="{ ...menu, ...tooltip }" v-bind="bind" fab dark color="pink" class="btn-top">
                    <v-icon>{{mdiGift}}</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-subheader class="overline">Gifts</v-subheader>
                  <v-list-item @click="() => null">
                    <v-list-item-icon><v-icon>{{mdiVideoVintage}}</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Group video</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="() => null">
                    <v-list-item-icon><v-icon>{{mdiCreditCardOutline}}</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Gift card</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="() => null">
                    <v-list-item-icon>
                      <img src="https://d3el26csp1xekx.cloudfront.net/staticDir/logo_icon_color_2019.svg" width="24" />
                    </v-list-item-icon>
                    <v-list-item-content class="d-block">
                      <img src="https://d3el26csp1xekx.cloudfront.net/staticDir/logo_black.svg" height="12" />
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </tooltip-btn>
            -->
          </div>

          <!-- Top-right column: insert new blob -->
          <div class="d-flex flex-column align-end btn-container-right" style="width: 64px;">
            <template v-if="!isPageReadonly">
              <v-btn
                v-if="mode != 'design' && allowReplaceDesign"
                v-blur
                fab dark color="pink darken-1" class="btn-top"
                :disabled="selectedPageI != 0"
                @click.stop="showReplaceDesign = true"
              >
                <v-icon v-html="mdiCards" />
                <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Change cover</span>
              </v-btn>

              <v-btn
                v-if="!card.skin?.hide_text_btn"
                v-blur
                class="btn-top"
                attach fab dark color="deep-purple"
                :disabled="!!selected"
                @click.stop="insertText(W / 2, H / 4)"
              >
                <v-icon v-html="mdiTextBox" />
                <span class="btn-tooltip left point" v-bind:class="{ persistent: showHelp }">Add message</span>
              </v-btn>

              <v-btn
                v-if="!card.skin?.hide_img_btn"
                v-blur
                class="btn-top"
                attach fab dark color="indigo"
                :disabled="!!selected && !(selected.template && selected.type == 'IMG' && mode != 'design')"
                @click.stop="promptUpload('image', uploadImageFromBtn)"
              >
                <v-icon v-html="mdiImage" />
                <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add image</span>
              </v-btn>

              <template v-if="(mode != 'design') && (!card.skin?.hide_video_btn)">
                <v-menu
                  v-if="isMediaRecorderSupported || hasGroupVideo || mode == 'purchase'"
                  offset-x left nudge-left="4"
                  v-model="videoMenuOpen"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-blur v-on="on"
                      class="btn-top"
                      fab dark color="blue"
                      :disabled="!!selected && !(selected.template && selected.type == 'VIDEO' && mode != 'design')"
                    >
                      <v-icon v-html="mdiVideoVintage" />
                      <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add video</span>
                    </v-btn>
                  </template>
                  <v-list class="py-0">
                    <template v-if="(hasGroupVideo || mode == 'purchase') && !selected?.template && !IS_IFRAME">
                      <v-list-item v-if="hasGroupVideo" @click="addToGroupVideo">
                        <v-list-item-icon><v-icon>{{mdiAccountGroup}}</v-icon></v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>Add to group video</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-else @click="$emit('click-add-group-video')">
                        <v-list-item-icon><v-icon>{{mdiAccountGroup}}</v-icon></v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>Add group video…</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider />
                    </template>
                    <template v-if="isMediaRecorderSupported">
                      <v-list-item @click.stop="promptRecordVideo">
                        <v-list-item-icon><v-icon>{{hasGroupVideo ? mdiAccount : mdiRecord}}</v-icon></v-list-item-icon>
                        <v-list-item-content><v-list-item-title>Record <span v-if="hasGroupVideo">standalone</span> video…</v-list-item-title></v-list-item-content>
                      </v-list-item>
                      <v-list-item @click="promptUpload('video', f => uploadVideo('new-blob', f))">
                        <v-list-item-icon><v-icon v-html="mdiUpload" /></v-list-item-icon>
                        <v-list-item-content><v-list-item-title>Upload <span v-if="hasGroupVideo">standalone</span> video…</v-list-item-title></v-list-item-content>
                      </v-list-item>
                    </template>
                    <v-list-item v-else @click="promptUpload('video', f => uploadVideo('new-blob', f))">
                      <v-list-item-icon><v-icon v-html="hasGroupVideo ? mdiAccount : mdiUpload" /></v-list-item-icon>
                      <v-list-item-content><v-list-item-title>Add <span v-if="hasGroupVideo">standalone</span> video</v-list-item-title></v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn
                  v-else
                  v-blur
                  class="btn-top"
                  fab dark color="blue"
                  :disabled="!!selected"
                  @click.stop="promptUpload('video', f => uploadVideo('new-blob', f))"
                >
                  <v-icon v-html="mdiVideoVintage" />
                  <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add video</span>
                </v-btn>
              </template>

              <v-btn
                v-if="(mode != 'design') && (!card.skin?.hide_gif_btn)"
                v-blur
                class="btn-top"
                fab dark color="cyan"
                :disabled="!!selected"
                @click.stop="showGiphy = true"
              >
                <v-icon v-html="mdiStickerEmoji" />
                <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add GIF / sticker</span>
              </v-btn>

              <template v-if="(mode != 'design') && maxGiftcardValue && (isOwner || !card.skin?.disable_contributor_giftcards) && (card.type != 'INVITATION')">
                <v-btn
                  v-blur
                  ref="add-gift-card-btn"
                  class="btn-top"
                  fab dark color="green lighten-2"
                  :disabled="!!selected"
                  @click.stop="
                    () => {
                      if (showGiftcardBlock)
                        $confirm({
                          title: 'Unsaved changes',
                          msg: `Please save your changes before adding ${giftcardBlobsEditable.length ? 'gift card value' : 'a gift card'}`,
                          buttons: [ 'Ok' ],
                          primary: 'Ok'
                        });
                      else if (giftcardBlobsEditable.length)
                        showGiftcardMultipleMenu = true;
                      else
                        showGiftcard = true;
                    }
                  "
                >
                  <v-icon v-html="mdiWalletGiftcard" />
                  <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add gift card</span>
                </v-btn>

                <v-menu
                  v-if="giftcardBlobsEditable.length && showGiftcardMultipleMenu"
                  offset-x left width="320" max-width="320"
                  :activator="$refs['add-gift-card-btn']"
                  v-model="showGiftcardMultipleMenu"
                >
                  <v-list class="primary--text text-body-2" dense>
                    <v-list-item-subtitle class="text--label-active px-3 pt-1 pb-2">
                      Add value to existing gift card
                    </v-list-item-subtitle>
                    <v-list-item
                      v-for="g in giftcardBlobsEditable" :key="'add-value-' + g.id"
                      class="li-pop simple px-3"
                      @click="$emit('click-giftcard', g)"
                    >
                      <v-img
                        class="v-card v-sheet mr-0"
                        v-bind:style="{ opacity: g.giftcard.claim_ids?.length ? .5 : 1 }"
                        width="42" min-width="42" max-width="42"
                        :aspect-ratio="300 / 190"
                        :src="g.giftcard.product_img"
                      />
                      <div
                        class="pl-0"
                        v-bind:class="{ 'grey--text text--lighten-1': g.giftcard.claim_ids?.length }"
                        style="max-width: calc(100% - 66px);"
                      >
                        <div
                          class="overline short"
                          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                          v-text="g.giftcard.product_name"
                        />
                        <div class="text--label-active" style="color: inherit;">
                          <currency :code="g.giftcard.cur" />{{g.giftcard.amt}}
                        </div>
                      </div>
                      <v-icon small v-html="mdiChevronRight" />
                    </v-list-item>
                    <v-divider class="my-2" />
                    <v-list-item class="li-pop simple px-3 py-4" @click="showGiftcard = true">
                      <v-icon style="padding: 0 9px;" v-html="mdiWalletGiftcard" />
                      <div class="px-0">Add new gift card</div>
                      <v-icon small v-html="mdiChevronRight" />
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <v-btn
                v-if="!card.skin?.hide_link_btn"
                v-blur
                class="btn-top"
                fab dark color="teal"
                :disabled="!!selected"
                @click.stop="showEditBtn = { text: 'Link', color: '#1976d2', scale: 1.5, url: '' }"
              >
                <v-icon v-html="mdiLinkVariant" />
                <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add link button</span>
              </v-btn>

              <template v-if="mode == 'design'">

                <v-menu offset-x left nudge-left="4">
                  <template v-slot:activator="{ on }">
                    <v-btn v-blur v-on="on" class="btn-top" fab dark color="blue" :disabled="!!selected">
                      <v-icon v-html="mdiImageEditOutline" />
                      <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add slot</span>
                    </v-btn>
                  </template>
                  <v-list class="pt-0" dense>
                    <v-subheader class="mb-n1">Content type</v-subheader>
                    <v-list-item @click="insertTemplateBlob_IMG">
                      <v-list-item-icon class="mr-4"><v-icon v-html="mdiImage" /></v-list-item-icon>
                      <v-list-item-title>Image</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="insertTemplateBlob_VIDEO">
                      <v-list-item-icon class="mr-4"><v-icon v-html="mdiVideoVintage" /></v-list-item-icon>
                      <v-list-item-title>Video</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-btn
                  v-blur
                  class="btn-top"
                  attach fab dark color="cyan darken-2"
                  :disabled="!!selected"
                  @click.stop="insertGradient"
                >
                  <v-icon v-html="mdiCircleHalfFull" />
                  <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add gradient</span>
                </v-btn>

                <!--
                <v-btn
                  v-blur fab class="btn-top"
                  v-bind:style="{
                    'background-color': selected && selected.type == 'TEXT' && selected.template == 'cover' ? 'rgba(25, 118, 210, .5) !important' : null
                  }"
                  :disabled="!!selected"
                  @click.stop="insertText(W / 2, H / 4, null, 'cover')"
                >
                  <v-icon>{{mdiTextBoxMultiple}}</v-icon>
                </v-btn>
                -->
                <!--
                <v-btn
                  v-blur
                  class="btn-top"
                  fab dark color="blue darken-3"
                  :disabled="!!selected"
                  @click.stop="insertDesignTemplateBlob"
                >
                  <v-icon v-html="iconPathImageSync" />
                  <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Add image slot</span>
                </v-btn>
                -->

                <v-btn
                  v-if="global.profile?.admin || global.profile?.designer"
                  v-blur
                  class="btn-top"
                  fab dark color="green"
                  @click.stop="$emit('toggle-media-library')"
                >
                  <v-icon v-html="mdiFolderMultipleImage" />
                  <span class="btn-tooltip left" v-bind:class="{ persistent: showHelp }">Toggle media library</span>
                </v-btn>

              </template>
            </template>
          </div>
        </div>

        <!-- Bottom (centered) -->
        <div class="flex-grow-1 d-flex justify-center align-end ma-n3 overflow-auto">

          <div
            v-if="!isCropping"
            class="d-flex pa-3"
            style="pointer-events: auto;"
            v-bind:style="{ 'margin-right': (ctrlPaddingRight || 0) + 'px' }"
          >

            <!-- The container with the click listener is necessary to prevent double-tap to zoom on the buttons if they become disabled: https://stackoverflow.com/a/54753520/8439453 -->
            <v-card
              class="btn-top ma-0 btn-group btn-group-horizontal v-btn--is-elevated"
              style="touch-action: manipulation; border-radius: 28px;"
              role="navigation" aria-label="Card navigation"
              :ripple="false"
              @click="() => null"
            >
              <v-btn
                class="btn-top ma-0 pl-3 pr-2"
                rounded height="56" elevation="0" aria-label="Previous page"
                :disabled="(selectedPageI == 0) || isCropping"
                @click="swipe(-1)"
              >
                <v-icon large :color="mode == 'purchase' ? null : 'primary'" v-html="mdiChevronLeft" />
                <span class="btn-tooltip top" style="margin-bottom: -6px;">Previous page</span>
              </v-btn>

              <v-btn
                v-if="isGridAllowed"
                class="btn-top ma-0"
                height="56" elevation="0"
                :disabled="isSelectedMessageHereText || isCropping"
                @click="e => e.target.getElementsByTagName('input')[0].focus()"
              >
                <form autocomplete="off" @submit.prevent>
                  <div class="relative">
                    <v-text-field
                      class="mt-0"
                      v-bind:style="{ width: (pages.length < 10 ? 36 : pages.length < 100 ? 52 : 72) + 'px' }"
                      hide-details dense autocomplete="-" type="tel" aria-label="Page number"
                      :rules="[ isValidPage ]"
                      v-model="pageInput"
                      @update:error="x => pageInputHasError = x"
                      @keydown.stop
                      @focus="e => e.target.select()"
                      @input="onPageInput"
                      @click.native.stop
                    />
                    <v-text-field
                      class="inline-suffix mt-0"
                      hide-details dense reverse readonly
                      :value="`/ ${pages.length}`"
                    />
                  </div>
                </form>
                <span class="btn-tooltip top">Jump to page</span>
              </v-btn>

              <template v-if="mode == 'view'">
                <v-btn
                  v-if="isGridAllowed"
                  class="btn-top ma-0"
                  height="56" elevation="0"
                  :aria-label="viewAs ? 'Show page stack' : 'Show page grid'"
                  @click="viewAs = viewAs ? null : 'grid'"
                >
                  <v-icon v-html="viewAs ? iconPathCardPages : iconPathPageGrid" />
                  <span class="btn-tooltip top">{{viewAs ? 'Show page stack' : 'Show page grid'}}</span>
                </v-btn>
              </template>

              <template v-else>
                <v-menu top nudge-left="80" nudge-top="2" width="320" offset-y v-model="showPageOptions">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      v-blur
                      class="btn-top ma-0"
                      height="56" elevation="0" aria-label="Page options"
                      :disabled="isSelectedMessageHereText || isCropping"
                    >
                      <v-icon v-html="iconPathPageSettings" />
                      <span class="btn-tooltip top">Page options</span>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <div class="text--label-active px-3 pb-1">View as</div>
                    <template
                      v-for="[ icon, title, isSelected, action ] in [
                        [ iconPathCardPages, 'Book', () => !viewAs, () => viewAs = null ],
                        [ mdiLayers, 'Layers', () => viewAs == 'layers', () => viewAs = 'layers' ],
                        [ iconPathPageGrid, 'Grid', () => viewAs == 'grid', () => viewAs = 'grid' ]
                      ]"
                    >
                      <v-list-item :input-value="isSelected()" :color="isSelected() ? 'primary' : null" @click="action">
                        <v-list-item-icon class="mr-3">
                          <v-icon v-html="icon" />
                        </v-list-item-icon>
                        <v-list-item-title v-text="title" />
                      </v-list-item>
                    </template>

                    <v-divider class="my-2" />

                    <v-menu z-index="204" right content-class="page-layout-menu" max-width="320" offset-x v-model="showAddNewPage">
                      <template v-slot:activator="{ on }">
                        <v-list-item v-on="on" :disabled="selectedPageI < lastDesignPageI">
                          <v-list-item-icon class="mr-3">
                            <v-icon v-html="iconPathAddCardPage" />
                          </v-list-item-icon>
                          <v-list-item-title>Add page after</v-list-item-title>
                          <v-list-item-icon>
                            <v-icon v-html="mdiChevronRight" />
                          </v-list-item-icon>
                        </v-list-item>
                      </template>
                      <div class="pa-3" style="height: 400px;">
                        <page-layout-picker :background="card.background" @select="x => addPage(selectedPageI, x)" />
                      </div>
                    </v-menu>
                    <v-list-item @click="duplicateCurrentPage">
                      <v-list-item-icon class="mr-3">
                        <v-icon v-html="iconPathDuplicateCardPage" />
                      </v-list-item-icon>
                      <v-list-item-title>Duplicate current page</v-list-item-title>
                    </v-list-item>
                    <v-list-item :disabled="!canDeleteSelectedPage" @click="tryDeletePage(selectedPageI)">
                      <v-list-item-icon class="mr-3">
                        <v-icon v-html="iconPathDeleteCardPage" />
                      </v-list-item-icon>
                      <v-list-item-title>Delete current page</v-list-item-title>
                    </v-list-item>

                    <v-divider class="my-2" />

                    <template v-if="isOwner">
                      <v-menu
                        z-index="204" content-class="white" right max-width="320" offset-x
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-list-item
                            v-on="on" v-blur
                            class="py-1"
                            :input-value="(card.background && (card.background.img || (card.background.color && (card.background.color != '#FFFFFF')))) ? 'primary' : null"
                            :color="(card.background && (card.background.img || (card.background.color && (card.background.color != '#FFFFFF')))) ? 'primary' : null"
                          >
                            <v-list-item-icon class="mr-3">
                              <v-icon v-html="mdiTexture" />
                            </v-list-item-icon>
                            <div>
                              <v-list-item-title>Background</v-list-item-title>
                              <v-list-item-subtitle class="grey--text">Applies to all pages</v-list-item-subtitle>
                            </div>
                            <v-list-item-icon>
                              <v-icon v-html="mdiChevronRight" />
                            </v-list-item-icon>
                          </v-list-item>
                        </template>
                        <div style="height: 400px;">
                          <v-list dense>
                            <div class="text--label-active px-3 pb-1">Custom</div>
                            <color-picker-btn :colors="colors" nudge-right="0" v-model="card.background.color">
                              <template v-slot="{ on }">
                                <v-list-item v-on="on" v-blur>
                                  <v-btn elevation="1" fab x-small :color="card.background.color || '#FFFFFF'" />
                                  <v-list-item-title class="ml-3">Color</v-list-item-title>
                                  <v-list-item-icon>
                                    <v-icon v-html="mdiChevronRight" />
                                  </v-list-item-icon>
                                </v-list-item>
                              </template>
                            </color-picker-btn>
                            <texture-menu-btn
                              :value="card.background.img"
                              :background-color="card.background.color || '#FFFFFF'"
                              @input="v => card.background.img = v"
                            >
                              <template v-slot:default="{ on }">
                                <v-list-item v-on="on" v-blur>
                                  <v-btn
                                    v-bind:style="{
                                      'background-image': card.background.img ? `url(${card.background.img})` : null
                                    }"
                                    elevation="1" fab x-small
                                    :color="card.background.color || '#FFFFFF'"
                                  />
                                  <v-list-item-title class="ml-3">Texture / image</v-list-item-title>
                                  <v-list-item-icon>
                                    <v-icon v-html="mdiChevronRight" />
                                  </v-list-item-icon>
                                </v-list-item>
                              </template>
                            </texture-menu-btn>
                          </v-list>
                          <v-divider />
                          <div class="pa-3">
                            <div class="text--label-active pb-2">Presets</div>
                            <v-row>
                              <v-col v-for="[ src, color ] in [ [ null, '#FFFFFF' ], ...BACKGROUNDS ]" :key="src" cols="4">
                                <v-card
                                  v-blur
                                  tile hover
                                  :color="color"
                                  @click="card.background.img = src; card.background.color = color;"
                                >
                                  <v-img :aspect-ratio="5 / 7" :src="src || '/template-placeholder.png'" />
                                </v-card>
                              </v-col>
                            </v-row>
                          </div>
                        </div>
                      </v-menu>
                      <v-divider class="my-2" />
                    </template>

                    <!--
                    <v-menu right offset-x>
                      <template v-slot:activator="{ on }">
                        <v-list-item v-on="on" :disabled="selectedPageI < lastDesignPageI">
                          <v-list-item-icon class="mr-3">
                            <v-icon v-html="iconPathAddCardPage" />
                          </v-list-item-icon>
                          <v-list-item-title>Snap to grid</v-list-item-title>
                          <v-list-item-icon>
                            <v-icon v-html="mdiChevronRight" />
                          </v-list-item-icon>
                        </v-list-item>
                      </template>
                      <v-list dense>
                        <template
                          v-for="[ title, value ] in [
                            [ 'Edges only', 0 ],
                            [ 'Sparse', 1 ],
                            [ 'Medium', 2 ],
                            [ 'Dense', 3 ],
                            [ 'Off', -1 ]
                          ]"
                        >
                          <v-list-item :input-value="alignGuides == value" @click="alignGuides = value">
                            <v-list-item-icon class="mr-3">
                              <v-icon :color="alignGuides == value ? null : 'transparent'" v-html="mdiCheck" />
                            </v-list-item-icon>
                            <v-list-item-title v-text="title" />
                          </v-list-item>
                        </template>
                      </v-list>
                    </v-menu>
                    -->
                    <v-list-item
                      :color="snapToGuides ? 'primary' : null"
                      :input-value="snapToGuides"
                      @click="alignGuides = !snapToGuides"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon v-html="mdiFormatHorizontalAlignCenter" />
                      </v-list-item-icon>
                      <v-list-item-title>Snap to guides</v-list-item-title>
                      <v-switch class="ml-6 mt-n1" hide-details readonly :input-value="snapToGuides" />
                    </v-list-item>
                    <!-- Warning: this doesn't work yet since 'showHelp' is actually a prop -->
                    <!--
                    <v-list-item :input-value="showHelp" @click="showHelp = !showHelp">
                      <v-list-item-icon class="mr-3">
                        <v-icon v-html="mdiHelpCircle" />
                      </v-list-item-icon>
                      <v-list-item-title>Show labels</v-list-item-title>
                      <v-switch
                        class="ml-6 mt-n1"
                        color="grey darken-2" hide-details readonly
                        :input-value="showHelp"
                      />
                    </v-list-item>
                    -->
                  </v-list>
                </v-menu>

                <v-btn
                  ref="ctx-menu"
                  v-blur
                  class="btn-top ma-0"
                  height="56" elevation="0" aria-label="Selected options"
                  :disabled="!selected || isCropping"
                  @click="e => contextMenu = { activator: $refs['ctx-menu'] }"
                >
                  <v-icon v-html="iconPathBlobSettings" />
                  <span class="btn-tooltip top">Selected options</span>
                </v-btn>
              </template>

              <v-menu top nudge-top="2" max-width="320" offset-y content-class="page-layout-menu">
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-show="(selectedPageI == pages.length - 1) && (mode != 'view')"
                    v-blur
                    v-on="on"
                    class="btn-top ma-0 pl-2 pr-3"
                    rounded height="56" elevation="0" aria-label="Add new page"
                    :disabled="isSelectedMessageHereText || isCropping || ((selectedPageI == pages.length - 1) && (mode == 'view'))"
                    @click="swipe(1)"
                  >
                    <v-icon large :color="mode == 'purchase' ? null : 'primary'" v-html="iconPathChevronRightPlus" />
                    <span class="btn-tooltip top" style="margin-bottom: -6px;">Add new page</span>
                  </v-btn>
                </template>
                <div class="pa-3" style="height: 400px;">
                  <page-layout-picker :background="card.background" @select="x => addPage(selectedPageI, x)" />
                </div>
              </v-menu>
              <v-btn
                v-if="!((selectedPageI == pages.length - 1) && (mode != 'view'))"
                v-blur
                class="btn-top ma-0 pl-2 pr-3"
                rounded height="56" elevation="0" aria-label="Next page"
                v-bind:class="{
                  wobble: !didInteract && selectedPageI == 0 && pages.length > 1 && $vuetify.breakpoint.xsOnly && mode == 'view'
                }"
                :disabled="isSelectedMessageHereText || isCropping || (selectedPageI == pages.length - 1)"
                @click="swipe(1)"
              >
                <v-icon large :color="mode == 'purchase' ? null : 'primary'" v-html="mdiChevronRight" />
                <span class="btn-tooltip top" style="margin-bottom: -6px;">Next page</span>
              </v-btn>
            </v-card>

            <template v-if="card.type == 'INVITATION'">
              <v-btn
                v-if="showEventDetails"
                class="btn-top ma-0 ml-3"
                style="min-width: unset;"
                rounded height="56" color="primary" aria-label="RSVP"
                @click="$emit('click-event-details')"
              >
                <v-icon v-html="mdiCalendarEdit" />
                <span class="ml-2 mr-1" v-bind:class="{ 'btn-tooltip top': $vuetify.breakpoint.xsOnly }">Event details…</span>
              </v-btn>
              <rsvp-btn
                v-else-if="(mode == 'view') || (mode == 'preview')"
                class="btn-top ma-0 ml-3"
                :pulse="!didClickRSVP"
                @click="didClickRSVP = true; $emit('click-rsvp');"
              />
            </template>

            <!-- This could be a good spot for the music button -->
            <!--
            <v-btn v-if="card.skin?.music" v-blur class="btn-top my-0" v-bind:class="{ pulse }" fab>
              <v-icon large v-html="mdiMusic" />
            </v-btn>
            -->
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.card-editor {
  min-height: 480px;
  width: 100%;
  height: 100%;
  position: relative;
}
.card-editor.preview {
  min-height: unset;
  pointer-events: none;
}
.disable-zoom {
  /* Leave this as 'pan-y' instead of 'none' so we can still scroll when the keyboard comes up */
  touch-action: pan-y !important;
}

.watermark {
  pointer-events: none;
  z-index: 99999;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' height='300px' width='280px'><text transform='translate(40, 300) rotate(-45)' fill='rgba(128,128,128,.3)' font-size='80' font-family='sans-serif' font-weight='bold'>SAMPLE</text></svg>");
  background-repeat: repeat;
}

.toolbar {
  position: absolute;
  z-index: 3;
}

.page-transition-container {
  position: absolute;
}
.page-transition-container.transition {
  transition: transform .2s, left .2s, opacity .2s, top .2s;
}
.page-container {
  transform-origin: 0 0;
}
.page-sheet {
  position: relative;
  background-repeat: repeat;
  background-color: white;
}

.page-sheet.will-hide {
  outline: 4px dashed #E0E0E0;
  /*opacity: .87;*/
}

.placeholder-page {
  z-index: 1;
  background-color: rgba(255, 255, 255, .87);
  backdrop-filter: blur(5px);
  -webpack-backdrop-filter: blur(5px);
}

.is-dragging {
  cursor: grabbing !important;
}

.is-selected-page {
  background-color: rgb(0, 0, 0, .13);
  border: 4px dashed rgba(0, 0, 0, .13);
  border-radius: 8px;
  padding: 8px !important;
}

.is-selected-page .page-grid-draggable-handle {
  bottom: -12px !important;
}

.page-layout-menu {
  width: 320px;
  max-height: 400px !important;
  background-color: rgb(255, 255, 255, .87);
  backdrop-filter: blur(5px);
  -webpack-backdrop-filter: blur(5px);
}

.drop-zone-background {
  background-color: rgba(0, 0, 0, 0.5);
}
.drop-zone {
  z-index: 9999;
}
.drop-zone.drop-zone-readonly {
  background-color: rgba(63, 63, 63, 0.5);
}
.drop-zone:not(.drop-zone-readonly) {
  background-color: rgba(33, 150, 243, 0.5);
}

.btn-container {
  position: relative;
  pointer-events: none;
  width: 100%;
  height: 100%;
}
.btn-container .main-btns {
  opacity: 0;
  transition: opacity .2s;
}
.btn-container .main-btns.show {
  opacity: 1;
}

.btn-container .btn-top {
  margin: 4px;
  position: relative;
}

/*
.btn-container .btn-container-left .btn-top,
.btn-container .btn-container-right .btn-top {
  border-radius: 0;
}
.btn-container .btn-container-left .btn-top:last-child {
  border-bottom-right-radius: 16px;
}
.btn-container .btn-container-right .btn-top:last-child {
  border-bottom-left-radius: 16px;
}
*/

.btn-container .theme--dark.v-btn.v-btn--disabled.btn-top:disabled {
  background-color: rgba(0, 0, 0, 0.12) !important;
  /*color: rgba(0, 0, 0, 0.26) !important;*/
}

.li-pop .v-image {
  transition: transform .3s;
}
.li-pop:hover .v-image {
  transform: scale(1.3);
}

.btn-container .btn-input {
  height: 56px;
  border-radius: 28px;
  margin-bottom: 5px;
  white-space: nowrap;
}

.text-effect-btn {
  width: 75px !important;
  height: 75px !important;
  border-radius: unset;
  text-transform: unset;
}

.layer-hidden {
  outline: 2.5px dashed #E0E0E0;
  opacity: .5;
}

.font-align-btn {
  position: absolute;
  right: -6px;
  bottom: -4px;
}
</style>

<style>
.btn-container button, .btn-container .btn-input {
  pointer-events: auto;
}
.btn-container .main-btns:not(.show) button,
.btn-container .main-btns:not(.show) .btn-input,
.btn-container button.v-btn--disabled {
  pointer-events: none;
}
.btn-container .btn-input .v-input__slot {
  padding: 0 !important;
}
.btn-container .btn-input .v-input .v-input__slot input {
  color: inherit;
  padding: 0 !important;
}
.btn-container .v-menu__content > *:first-child {
  pointer-events: auto;
  background-color: rgba(255,255,255,.9) !important;
}

.text-effect-btn > .v-btn__content {
  flex-direction: column;
}

.frame svg.bubble path {
  pointer-events: auto;
}

.preview-selected .card-preview-page > * > * {
  outline: 8px dashed rgb(33,150,243);
}
.preview-selected.selected-owner .card-preview-page > * > * {
  outline: 8px dashed red;
}

.open .page-sheet.wobble {
  animation: page-wobble 8s ease-in-out infinite;
}
@keyframes page-wobble {
  0% { transform: translateX(0); opacity: 1; }
  30% { transform: translateX(0); opacity: 1; }
  35% { transform: translateX(-5%); opacity: .8; }
  40% { transform: translateX(0); opacity: 1; }
  45% { transform: translateX(-12%); opacity: .8; }
  55% { transform: translateX(-12%); opacity: .8; }
  60% { transform: translateX(0); opacity: 1; }
  100% { transform: translateX(0); opacity: 1; }
}

.open .btn-top.wobble:not(:hover):before {
  animation: btn-wobble 8s ease-in-out infinite;
}
@keyframes btn-wobble {
  0% { opacity: 0; }
  30% { opacity: 0; }
  35% { opacity: .24; }
  40% { opacity: 0; }
  45% { opacity: .24; }
  55% { opacity: .24; }
  60% { opacity: 0; }
  100% { opacity: 0; }
}
</style>

<script>
import Blob from '@/components/Blob.vue';
import BlobFrame from '@/components/BlobFrame.vue';
import CardPreview from '@/components/CardPreview.vue';
import ColorPickerBtn from '@/components/ColorPickerBtn.vue';
import Currency from '@/components/Currency.vue';
import Draggable from 'vuedraggable';
import FontPickerBtn from '@/components/FontPickerBtn.vue';
import FrameBtn from '@/components/FrameBtn.vue';
import IntField from '@/components/IntField.vue';
import PageLayoutPicker from '@/components/PageLayoutPicker.vue';
import RsvpBtn from '@/components/RsvpBtn.vue';
import TextureMenuBtn from '@/components/TextureMenuBtn.vue';
import VResponsiveBounds from '@/components/VResponsiveBounds.vue';
import { publicDesign, blankDesignId, nullCoverDesignId } from '@/mixins/public-design.js';
import uuid4 from '@/utils/uuid.js';
import { getMetadata } from '@/utils/video.js';
import { fonts, colors, frames, lightColors, textSizeRange } from '@/card-customization-options.js';
import constraints from '@/card-geometry-constraints.js';
import { isBlobVisible, isBlobEditable, shouldSaveBlob, isZoomed, canTransform, setTemplateBlob } from '@/utils/edit-order.js';
import { FORMATTERS, getEventFields, updateEventLinks } from '@/utils/event-links.js';
import { findGroupVideoBlob, findGroupVideoBlobPageI, removeGroupVideoText } from '@/utils/group-video.js';
import { getAverageColor, getRandomContrastingColor } from '@/utils/group-video-template.js';
import { getImgDimensions, processImageFile, scale as scaleBounds } from '@/utils/image.js';
import { TEXT_EFFECTS, TEXT_EFFECTS_COMBINE, TEXT_EFFECTS_PRESERVE_KEYS } from '@/utils/text-effects.js';
import iconPathAddCardPage from 'raw-loader!@/assets/icon-path-add-card-page.txt';
import iconPathBlobSettings from 'raw-loader!@/assets/icon-path-blob-settings.txt';
import iconPathChevronRightPlus from 'raw-loader!@/assets/icon-path-chevron-right-plus.txt';
import iconPathDeleteCardPage from 'raw-loader!@/assets/icon-path-delete-card-page.txt';
import iconPathDuplicateCardPage from 'raw-loader!@/assets/icon-path-duplicate-card-page.txt';
import iconPathFitToPage from 'raw-loader!@/assets/icon-path-fit-to-page.txt';
import iconPathFrame from 'raw-loader!@/assets/icon-path-frame.txt';
import iconPathPageBackground from 'raw-loader!@/assets/icon-path-page-background.txt';
import iconPathPageGrid from 'raw-loader!@/assets/icon-path-page-grid.txt';
import iconPathPageGridLayers from 'raw-loader!@/assets/icon-path-page-grid-layers.txt';
import iconPathPageSettings from 'raw-loader!@/assets/icon-path-page-settings.txt';
import iconPathCardPages from 'raw-loader!@/assets/icon-path-card-pages.txt';
import iconPathUnfitToPage from 'raw-loader!@/assets/icon-path-unfit-to-page.txt';
import { mdiFormatFont, mdiPalette, mdiFormatSize, mdiFormatAlignJustify, mdiFormatAlignLeft, mdiFormatAlignCenter, mdiFormatAlignRight, mdiTextBox, mdiImage, mdiCircleHalfFull, mdiChevronLeft, mdiPageLayoutBody, mdiUpload, mdiUploadOff, mdiChevronRight, mdiFileOutline, mdiClose, mdiFilePlus, mdiHelp, mdiStickerEmoji, mdiVideoVintage, mdiGift, mdiAccount, mdiCreditCardOutline, mdiAccountGroup, mdiWalletGiftcard, mdiCards, mdiFormatVerticalAlignTop, mdiFormatVerticalAlignCenter, mdiFormatVerticalAlignBottom, mdiPencil, mdiLinkVariant, mdiOpenInNew, mdiOrderBoolDescending, mdiOrderBoolAscendingVariant, mdiDelete, mdiArrowAll, mdiReload, mdiCalendarEdit, mdiLayers, mdiLayersOff, mdiArrowSplitHorizontal, mdiLock, mdiArrangeBringForward, mdiArrangeSendBackward, mdiContentCopy, mdiSelection, mdiFormatHorizontalAlignCenter, mdiTextShadow, mdiFormatAlignTop, mdiHelpCircle, mdiTexture, mdiFolderMultipleImage, mdiEye, mdiEyeOff, mdiCrop, mdiArrowUpThin, mdiImageEditOutline, mdiRecord, mdiRadioboxBlank, mdiRadioboxMarked, mdiImageMultiple, mdiAlert, mdiArrowUpBold, mdiCounter, mdiToggleSwitchOff } from '@mdi/js';

// Shorthand for referencing the page constraints (we use these often)
const { W, H } = constraints.page;
const constrain = constraints.constrain;

const BACKGROUNDS = [
  [ 'aged-1', '#FFECC0' ],
  [ 'aged-2', '#FFECC0' ],
  [ 'braided', '#FFFFFF' ],
  [ 'gold', '#FFD44D' ],
  [ 'cardboard', '#A58D69' ],
  [ 'dark-paper', '#3B3A44' ],
  [ 'gold-paint', '#FFD663' ],
  [ 'heavy', '#DDDDDD' ]
].map(([ name, color ]) => [ '//cdn.ellacard.com/page-textures/' + name + '.png.webp', color ]);

const READABLE_EVENT_LINK_FIELDS = {
  name: 'name',
  date_start: 'date',
  time_start: 'time',
  location: 'location name',
  address: 'address',
  url: 'link'
};

// Don't allow swipe gestures to process immediately after pinch / zoom gestures to give the user time to lift both
// fingers off the screen
const SWIPE_AFTER_GESTURE_DELAY_MS = 500;

function handleGesture(e, handler) {
  if (e.touches.length > 1)
    e.preventDefault();
  if (e.touches.length != 2)
    return;
  const x = e.touches[1].clientX - e.touches[0].clientX;
  const y = e.touches[1].clientY - e.touches[0].clientY;
  const distance = Math.sqrt(x * x + y * y);
  let angle = Math.atan2(y, x) * 180 / Math.PI;
  angle = (angle < 0) ? 360 + angle : angle;
  handler(distance, angle);
}

function resetTemplateBlob(b) {
  b[ b.type.toLowerCase() ].src = b.mask.placeholder;
  for (const key of [ 'x', 'y', 'w', 'h', 'r' ])
    b[key] = b.mask[key];
  b.user_id = null;
}

function stripForDesignId(page) {
  for (const blob of page.blobs)
    delete blob.for_design_id;
  return page;
}

function getImgScale(w, h) {
  // Images are constrained to fit 300x400 bounding box and abide by our minimum dimensions
  const wScale = constrain(1, constraints.blob.IMG.MIN_W / w, 300 / w);
  const hScale = constrain(1, constraints.blob.IMG.MIN_H / w, 400 / w);
  return (w > h) ? wScale : hScale;
}

export default {
  name: 'card-editor',

  mixins: [ publicDesign ],

  components: {
    Blob,
    BlobFrame,
    CardPreview,
    ColorPickerBtn,
    Currency,
    Draggable,
    FontPickerBtn,
    FrameBtn,
    IntField,
    DesignBlobListOptions: () => import('@/components/DesignBlobListOptions.vue'),
    GiftcardDialog: () => import('@/components/GiftcardDialog.vue'),
    TenorDialog: () => import('@/components/TenorDialog.vue'),
    LinkBtnDialog: () => import('@/components/LinkBtnDialog.vue'),
    RecordVideoDialog: () => import('@/components/RecordVideoDialog.vue'),
    ReplaceDesignDialog: () => import('@/components/ReplaceDesignDialog.vue'),
    PageLayoutPicker,
    RsvpBtn,
    TextureMenuBtn,
    VResponsiveBounds
  },

  props: {
    orderId: String,
    card: Object,
    isOwner: Boolean,
    mode: String, // design, purchase, edit, view, preview
    splayPages: { type: Boolean, default: true }, // styles to go along with the card-open animations
    showButtons: { type: Boolean, default: true },
    coverOnly: Boolean,
    hasGroupVideo: Boolean,
    maxGiftcardValue: { type: Number, default: 500000 },
    allowReplaceDesign: Boolean,
    paddingTop: { type: Number, default: 0 },
    gridPadding: { type: Number, default: 0 },
    ctrlPaddingRight: { type: Number, default: 0 },
    showHelp: Boolean,
    isVerifiedRecipient: Boolean,
    showEventDetails: Boolean,
    showGiftcardBlock: Boolean,
    showAutomationOpts: Boolean
  },

  data() {
    return {
      selectedPageI: 0,
      selected: null,
      isRootTransformInProgress: false,
      blobTransformsInProgress: {},
      disablePageTransition: false,

      pageRect: {
        scale: 1,
        w: 0,
        h: 0
      },

      guide: {
        horizontal: -1,
        vertical: -1
      },
      ctrlDown: false,
      alignGuides: true,

      lastDrag: null,
      isDragging: false,
      checkIsDragging: null,
      dragOverPage: null,
      autoSwipeOnDrag: null,
      didInteract: false,
      isSwiping: false,
      gestureStart: null,
      showEditBtn: null,
      showGiphy: false,
      showRecord: false,
      showGiftcard: false,
      showGiftcardMultipleMenu: false,
      showReplaceDesign: false,
      showPageOptions: false,
      showAddNewPage: false,
      videoMenuOpen: false,
      contextMenu: null,
      didClickRSVP: false,
      viewAs: null, // 'grid' | 'layers'
      selectedPersonalizationKey: null,
      selectedPersonalizationEnabled: false,
      selectedTextCSSDefaults: {},

      uploadImageStatus: this.$track('uploadImage'),
      uploadVideoStatus: this.$track('uploadVideo'),
      onDropStatus: this.$track('onDrop'),

      loadSelectedSignatureStatus: this.$track('loadSelectedSignature'),
      selectedSignature: {},

      transcodeT: 0,
      transcodeVideoStatus: this.$track('transcodeVideo'),

      alignObjectMenu: false,
      fontAlignmentMenu: false,
      showSmartText: false,

      pageInput: 1,
      pageInputHasError: false,

      fixedLastPage: null,
      selectedPageLayers: [],

      isCropping: false,
      didHideCropTip: false,
      showFontSizeSlider: true
    };
  },

  computed: {
    selectedOptions() {
      return this.selected ? this.selected[ this.selected.type.toLowerCase() ] : null;
    },

    isTransformInProgress() {
      return this.isRootTransformInProgress || !!Object.keys(this.blobTransformsInProgress).length || this.isCropping;
    },

    isPreview() {
      return this.mode === 'preview';
    },

    isPageReadonly() {
      return [ 'view', 'preview' ].includes(this.mode) || !!(this.fixedLastPage && (this.selectedPageI >= this.card.pages.length));
    },

    canDrag() {
      return !this.isPageReadonly && !this.viewAs;
    },

    userId() {
      if (this.mode == 'design')
        return null;
      if (this.global.profile?.org_admin && (this.global.profile.org_id == this.card.org_id) && this.$route.query.admin)
        return this.card.user_id || '-';
      return this.globalUserId || '-';
    },

    canEditSelected() {
      return !!this.selected && (!!this.selected.user_id || (this.mode == 'design')) && (this.isPrimary(this.selected) || this.isOwner);
    },

    canTransformSelected() {
      return this.canEditSelected && canTransform(this.selected, this.mode);
    },

    hasPageRect() {
      return !!(this.pageRect.w && this.pageRect.h);
    },

    isMediaRecorderSupported() {
      return !!window.MediaRecorder && !IS_MOBILE;
    },

    selectedPage() {
      return this.pages[this.selectedPageI];
    },

    selectedPageVisibleBlobs() {
      return this.selectedPage ? this.selectedPage.blobs.filter(this.isBlobVisible) : [];
    },

    selectedI() {
      if (!this.selected || !this.selectedPage)
        return -1;
      return this.selectedPage.blobs.findIndex(x => x === this.selected);
    },

    changeBlobPageD() {
      if (!this.selected)
        return 0;
      if (this.selected.x == 0)
        return -1;
      if (this.selected.x == 600)
        return 1;
      return 0;
    },

    lastDesignPageI() {
      if (this.mode != 'design') {
        // Any design pages will always be at the beginning of the card
        for (const i in this.card.pages) {
          const page = this.card.pages[i];
          if (page.design)
            continue;
          // We can figure out if a page is part of the design by looking at its blobs
          const blobs = this.card.pages[i].blobs.filter(this.isBlobVisible);
          if (blobs.find(x => x.template == 'page') || !blobs.find(x => !x.user_id || (x.template == 'cover')))
            return i - 1;
        }
      }
      // There are no non-design pages
      return this.card.pages.length - 1;
    },

    canDeleteSelectedPage() {
      if (this.isPageReadonly)
        return false;
      if (this.card.pages.length <= 1)
        // The card must have at least one editable page
        return false;
      if ((this.selectedPageI <= this.lastDesignPageI) && (this.mode != 'design'))
        // This page is part of the design
        return false;
      if (this.showAddNewPage)
        return false;
      if (this.isOwner)
        return true;
      // Normal users can only delete a page if they own every blob on the page
      for (const blob of this.selectedPage.blobs)
        if (blob.user_id && (blob.user_id != this.userId))
          return false;
      return true;
    },

    snapToGuides() {
      return this.ctrlDown ^ this.alignGuides;
    },

    pages() {
      if (this.fixedLastPage)
        return [ ...this.card.pages, this.fixedLastPage ];
      return [ ...this.card.pages ];
    },

    selectedPageLayersCondition() {
      return [ this.viewAs, this.selectedPage?.blobs ];
    },

    isSelectedMessageHereText() {
      return this.selected?.text?.text == 'Message here';
    },

    isSelectedLightColor() {
      return lightColors.includes(this.selected?.text?.color);
    },

    ignoreInput() {
      return this.isTransformInProgress || this.contextMenu || this.showRecord;
    },

    activeEffectNames() {
      // Warning: this makes the assumption that none of our text effects are subsets of other effects (unless they are
      // meant to be combined), including the combined variations of those (it.s why you used '.021em' as the 'Bold'
      // variation for the 'Outline' effect instead of '.02em')
      const names = new Set();
      let active = this.selected?.text?.css;
      if (!active)
        return names;
      for (const { css, combine, name } of TEXT_EFFECTS) {
        if (!css)
          continue;
        if (_.isMatch(active, css)) {
          names.add(name);
        } else if (combine) {
          for (const [ combineName, combineCSS ] of Object.entries(combine)) {
            if (_.isMatch(active, { ...css, ...combineCSS })) {
              names.add(name);
              names.add(combineName);
            }
          }
        }
      }
      return names;
    },

    isGridAllowed() {
      // Disable for invitations because of the extra room required for the RSVP button on mobile
      return (this.pages.length > 4) && (this.card.type != 'INVITATION');
    },

    views() {
      const grid = (this.mode != 'view') || this.isGridAllowed;
      const layers = (this.mode != 'view');
      return [ null, ...(layers ? [ 'layers' ] : []), ...(grid ? [ 'grid' ] : []) ];
    },

    eventLinkFieldsReadable() {
      const readable = new Set();
      for (const formatter of getEventFields(this.selected.text.event_link.format))
        for (const field of (FORMATTERS[formatter] ? FORMATTERS[formatter][0] : [ formatter ]))
          if (READABLE_EVENT_LINK_FIELDS[field])
            readable.add(READABLE_EVENT_LINK_FIELDS[field]);
      const output = Array.from(readable);
      if (output.length > 1)
        output[ output.length - 1 ] = 'and ' + output[ output.length - 1 ];
      return output.join((output.length > 2) ? ', ' : ' ');
    },

    averageBackgroundColorPromise() {
      return getAverageColor(this.card.background);
    },

    giftcardBlobs() {
      const blobs = [];
      for (const page of this.pages)
        for (const blob of page.blobs)
          if (blob.type == 'GIFTCARD')
            blobs.push(blob);
      return blobs;
    },

    giftcardBlobsEditable() {
      if (this.isOwner)
        return this.giftcardBlobs;
      if (this.card.skin?.disable_contributor_giftcards)
        return [];
      if (this.card.skin?.hide_giftcard_totals || this.card.skin?.private_pages)
        return this.giftcardBlobs.filter(x => x.user_id == this.userId);
      return this.giftcardBlobs;
    }
  },

  created() {
    Object.assign(this, {
      W, H,
      TEXT_EFFECTS,
      BACKGROUNDS,
      colors,
      fonts,
      frames,
      textSizeRange,

      isZoomed,

      iconPathAddCardPage,
      iconPathBlobSettings,
      iconPathChevronRightPlus,
      iconPathDeleteCardPage,
      iconPathDuplicateCardPage,
      iconPathFrame,
      iconPathPageBackground,
      iconPathPageGrid,
      iconPathPageGridLayers,
      iconPathCardPages,
      mdiFormatFont,
      mdiPalette,
      mdiFormatSize,
      mdiFormatAlignJustify,
      mdiFormatAlignLeft,
      mdiFormatAlignCenter,
      mdiFormatAlignRight,
      mdiTextBox,
      mdiImage,
      mdiCircleHalfFull,
      mdiChevronLeft,
      mdiPageLayoutBody,
      mdiUpload,
      mdiUploadOff,
      mdiChevronRight,
      mdiFileOutline,
      mdiClose,
      mdiFilePlus,
      mdiHelp,
      mdiStickerEmoji,
      mdiVideoVintage,
      mdiGift,
      mdiAccount,
      mdiCreditCardOutline,
      mdiAccountGroup,
      mdiWalletGiftcard,
      mdiCards,
      mdiFormatVerticalAlignTop,
      mdiFormatVerticalAlignCenter,
      mdiFormatVerticalAlignBottom,
      mdiPencil,
      mdiLinkVariant,
      mdiOpenInNew,
      mdiOrderBoolDescending,
      mdiOrderBoolAscendingVariant,
      mdiDelete,
      mdiArrowAll,
      mdiReload,
      mdiCalendarEdit,
      mdiLayers,
      mdiLayersOff,
      mdiArrowSplitHorizontal,
      mdiLock,
      mdiArrangeBringForward,
      mdiArrangeSendBackward,
      mdiContentCopy,
      mdiSelection,
      mdiFormatHorizontalAlignCenter,
      mdiTextShadow,
      mdiFormatAlignTop,
      mdiHelpCircle,
      mdiTexture,
      mdiFolderMultipleImage,
      iconPathFitToPage,
      iconPathUnfitToPage,
      iconPathPageSettings,
      mdiEye,
      mdiEyeOff,
      mdiCrop,
      mdiArrowUpThin,
      mdiImageEditOutline,
      mdiRecord,
      mdiRadioboxBlank,
      mdiRadioboxMarked,
      mdiImageMultiple,
      mdiAlert,
      mdiArrowUpBold,
      mdiCounter,
      mdiToggleSwitchOff
    });
  },

  async mounted() {
    if (!this.isPreview) {
      document.body.addEventListener('click', this.onClickOutside);
      document.body.addEventListener('keydown', this.onKeyDown);
      document.body.addEventListener('keydown', this.onKeyDownCapture, true);
      document.body.addEventListener('keyup', this.onKeyUp);
      document.body.addEventListener('paste', this.onPaste);
    }
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs['pages-container']);
  },

  beforeUnmount() {
    document.body.removeEventListener('click', this.onClickOutside);
    document.body.removeEventListener('keydown', this.onKeyDown);
    document.body.removeEventListener('keydown', this.onKeyDownCapture);
    document.body.removeEventListener('keyup', this.onKeyUp);
    document.body.removeEventListener('paste', this.onPaste);
    clearInterval(this.checkIsDragging);
    clearTimeout(this.transcodeTimeout);
    this.resizeObserver.unobserve(this.$refs['pages-container']);
  },

  methods: {
    isTransparent(blob) {
      return blob.hasOwnProperty('opacity') && !blob.opacity;
    },

    stripAlpha(color) {
      // To extract just the RGB component from a six-character hexa color string
      // TODO: this is duplicated in the color-picked-btn
      return /^#[0-9a-f]{8}$/i.test(color) ? color.substr(0, 7) : color;
    },

    getPageOffset(pageI) {
      const byScale = this.splayPages ? this.getScale(pageI) * this.pageRect.w * (pageI - this.selectedPageI) / 2 : 0;
      return byScale - (this.pageRect.w / 2);
    },

    willHide(pageI) {
      if (![ 'purchase', 'edit' ].includes(this.mode))
        return false;
      for (const b of this.pages[pageI].blobs)
        if (this.isBlobVisible(b, 'view'))
          return false;
      return true;
    },

    // This function is called externally and will change the page selection without any animation
    async setSelectedPageI(pageIndex) {
      this.disablePageTransition = true;
      await this.$nextTick();
      this.selectedPageI = pageIndex;
      await this.$nextTick();
      this.disablePageTransition = false;
    },

    registerDrag() {
      this.isDragging = true;
      this.lastDrag = new Date().getTime();
      this.checkIsDragging = this.checkIsDragging || setInterval(
        () => {
          if (new Date().getTime() - this.lastDrag > 100) {
            clearInterval(this.checkIsDragging);
            this.isDragging = false;
            this.checkIsDragging = null;
            this.$forceUpdate();
          }
        },
        100
      );
    },

    onDragEnter(pageI) {
      clearTimeout(this.autoSwipeOnDrag);
      this.autoSwipeOnDrag = setTimeout(
        () => {
          if (this.dragOverPage == pageI)
            this.swipe(pageI - this.selectedPageI);
        },
        500
      );
    },

    async onDrop(i, e) {
      clearTimeout(this.autoSwipeOnDrag);
      if (i != this.selectedPageI)
        return;

      if (this.isPageReadonly)
        return;

      // We accept two kinds of dropped things:
      // - Files (images / videos)
      // - An HTML snippet containing an img

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // TODO: handle multiple files
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.uploadImage('new-blob', file);
          return;
        }
        if (file.type.startsWith('video/')) {
          this.uploadVideo('new-blob', file);
          return;
        }
        throw new VisibleError('Can only drop images / videos');
      }

      const html = e.dataTransfer.getData('text/html');
      if (html) {
        const draggedDOM = (new DOMParser()).parseFromString(html, 'text/html');
        const imgs = draggedDOM.getElementsByTagName('img');
        if (imgs && imgs.length > 0) {
          const { w, h } = await getImgDimensions(imgs[0].src);
          this.insertImage(imgs[0].src, w, h);
          return;
        }
      }

      throw new VisibleError('Can only drop images / videos');
    },

    async onPaste(e) {
      if (!this.isActive())
        return;

      e.preventDefault();
      e.stopPropagation();

      const files = e.clipboardData.files;
      if (files && files.length > 0) {
        // TODO: handle multiple files
        await this.uploadImage('new-blob', e.clipboardData.files[0]);
        return;
      }

      const plain = e.clipboardData.getData('text/plain');
      if (plain) {
        this.insertText(W / 2, H / 2, plain);
        return;
      }

      const html = e.clipboardData.getData('text/html');
      const parsedDOM = (new DOMParser()).parseFromString(html, 'text/html');
      const imgs = parsedDOM.getElementsByTagName('img');
      if (imgs && imgs.length > 0) {
        const { w, h } = await getImgDimensions(imgs[0].src);
        await this.insertImage(imgs[0].src, w, h);
        return;
      }
    },

    getScale(i) {
      // Used for scaling / positioning pages falling away from the center selected page
      return 5 / (5 + Math.abs(i - this.selectedPageI));
    },

    async tryDeleteBlob(pageIndex, blobIndex) {
      const blob = this.pages[pageIndex].blobs[blobIndex];

      if (blob.type == 'GROUP_VIDEO') {
        let isDirty = false;
        try {
          const response = await this.$call('get-group-video-project', { project_id: blob.group_video.project_id });
          isDirty = Object.values(response.sections).find(x => x.inputs.length > 0);
        } catch (e) {
          // Any errors with this call should not impact our ability to remove the video since it's just a courtesy
          console.error(e);
        }
        const confirm = await this.$confirm({
          title: 'Are you sure?',
          msg: `This will remove the <span class="error--text">entire group video</span> from your order${isDirty ? ', and it looks like you <span class="error--text">already modified it</span>' : ''}. Are you sure you want to continue?`
        });
        if (confirm == 'No')
          return;

        // Make sure we also get the associated message blob - note that we do this silently without emitting a
        // 'delete-blob' of 'input' event, since we're just about to emit those events for the group video blob itself.
        const i = removeGroupVideoText(this.pages[pageIndex]);
        if (i < blobIndex)
          blobIndex--;
      }

      else if (!this.isPrimary(blob)) {
        // Presumably, if we're not the owner and we're calling this function, we should have the blob selected, just
        // being defensive with this 'suffix' check
        let suffix = '.';
        if (blob == this.selected) {
          const email = this.$options.filters.email(this.selectedSignature.email);
          if (email)
            suffix = ': ' + email;
        }
        const confirm = await this.$confirm({
          title: 'Are you sure?',
          msg: `Are you sure you want to delete this? ${blob.template ? "It's part of a template that" : 'It'} was created by someone else${suffix}`
        });
        if (confirm == 'No')
          return;
      }

      this.deleteBlob(pageIndex, blobIndex);
    },

    async deleteBlob(pageIndex, blobIndex, forceTemplate=false) {
      const blob = this.pages[pageIndex].blobs[blobIndex];
      if (!blob)
        return;
      if (blob.template && !forceTemplate && this.mode != 'design') {
        if (blob.type == 'TEXT') {
          blob.text.text = '';
        } else if (blob.mask) {
          blob.x = blob.mask.x;
          blob.y = blob.mask.y;
          blob.w = blob.mask.w;
          blob.h = blob.mask.h;
          blob.r = blob.mask.r;
          blob[ blob.type.toLowerCase() ].src = blob.mask.placeholder;
        }
      } else {
        this.pages[pageIndex].blobs.splice(blobIndex, 1);
        this.adjustPages();
        this.selected = null;
        await this.$nextTick();
        this.$emit('delete-blob', blob, pageIndex);
      }
      this.$emit('input', this.card);
    },

    async tryDeletePage(i) {
      const page = this.pages[i];
      if (page.blobs.find(x => x.type == 'GROUP_VIDEO'))
        return this.$confirm({
          title: 'Not allowed',
          msg: `You can't delete the page that contains your group video project.`,
          buttons: [ 'Ok' ],
          primary: 'Ok'
        });
      if ((this.mode == 'edit') && page.blobs.find(x => x.type == 'GIFTCARD'))
        return this.$confirm({
          title: 'Not allowed',
          msg: `You can't delete pages that contain gift cards.`,
          buttons: [ 'Ok' ],
          primary: 'Ok'
        });
      const hasOther = page.blobs.find(x => x.user_id && (x.user_id != this.userId));
      const confirmed = await this.$confirm({
        title: 'Are you sure?',
        msg: `Are you sure you want to delete this page?${hasOther ? ' It contains content that was added by someone else.' : ''}`
      });
      if (confirmed == 'No')
        return;

      // This is a kludge - the RSVP form is stored as a hidden blob because we used to be able to reposition it as a
      // button within the card. If this page contains the button, we need to make sure the button is moved out to a
      // page that is remaining.
      const formBlob = page.blobs.find(x => x.btn?.form);

      // In purchase mode, any blobs with for_design_id set need to be pushed down to successive pages
      if (this.mode == 'purchase') {
        let previousDesignBlobs = [];
        for (let j = i; j < this.card.pages.length; j++) {
          const [ design, native ] = _.partition(this.card.pages[j].blobs, x => x.for_design_id);
          this.card.pages[j].blobs = [ ...previousDesignBlobs, ...native ];
          previousDesignBlobs = design;
        }
      }

      this.card.pages.splice(i, 1);
      this.$emit('delete-page', page);
      this.$emit('input', this.card);
      this.adjustPages();

      if (formBlob) {
        this.insertBlob(formBlob, true);
        this.$emit('save');
      }
    },

    insertBlob(blob, didChangeBlobPage=false) {
      this.selectedPage.blobs.push(blob);
      this.selected = blob;
      this.$emit('insert-blob', this.selectedPageI, blob, didChangeBlobPage);
      shouldSaveBlob(blob) && this.$emit('input', this.card);
    },

    adjustPages() {
      const pages = this.card.pages;
      if (pages.length == 0)
        this.card.pages.push({ id: uuid4(), blobs: [], design: this.mode == 'design' ? true : undefined });
      if (this.selectedPageI >= pages.length)
        this.selectedPageI = pages.length - 1;
    },

    async updateTextEventLink(disabled) {
      if (this.selected.text.event_link.disabled != disabled) {
        this.$set(this.selected.text.event_link, 'disabled', disabled);
        updateEventLinks(this.card, this.card.event);
      }
      this.showSmartText = false;
      const selected = this.selected;
      await this.$nextTick();
      this.selected = selected;
      if (disabled)
        this.$refs[this.selected.id][0].focus(true);
    },

    insertText(x, y, msg, template) {
      if (this.card.skin?.hide_text_btn)
        return;

      const color = getRandomContrastingColor(this.averageBackgroundColor || '#FFF', colors);
      const defaults = { font: fonts[ Math.floor(Math.random() * fonts.length) ], color };

      // Try to copy the default settings from any other blobs the user has added or from any cover template blobs if
      // we're the owner. Note that we check the selected page first.
      for (const blob of this.selectedPage.blobs.slice().reverse()) {
        if ((blob.type == 'TEXT') && ((blob.user_id == this.userId) || (this.isOwner && (blob.template == 'cover')))) {
          Object.assign(defaults, _.pick(blob.text, [ 'font', 'color', 'css', 'frame', 'frame_options' ]));
          break;
        }
      }

      // 'msg' and 'template' are optional
      const blob = {
        id: uuid4(),
        user_id: this.userId,
        x, y,
        w: 360,
        h: 240,
        r: 0,
        template,
        type: 'TEXT',
        text: {
          ...defaults,
          size: 480,
          align: 'center',
          valign: 'center',
          text: msg || 'Message here'
        }
      };
      this.insertBlob(blob);
      this.$nextTick(() => this.$refs[blob.id][0].focus());
    },

    buildCenteredBlob(w, h, type, props) {
      // Creates a new blob with the specified dimensions in the center of the page
      // The 'props' should contain the 'type' and any other corresponding properties
      const blob = {
        id: uuid4(),
        user_id: this.userId,
        x: W / 2,
        y: H / 2,
        w,
        h,
        r: 0,
        type
      };
      Object.assign(blob, props);
      return blob;
    },

    addToGroupVideo() {
      this.selectedPageI = findGroupVideoBlobPageI(this.card);
      const blob = findGroupVideoBlob(this.card);
      this.$emit('click-group-video', blob);
    },

    promptRecordVideo() {
      this.videoMenuOpen = false;
      this.showRecord = true;
    },

    async onRecordInput(file, settings) {
      this.showRecord = false;
      if (!file)
        return;
      const { w, h, mirror } = settings;
      const src = URL.createObjectURL(file);
      if (this.selected?.template && (this.selected.type == 'VIDEO')) {
        const m = this.selected.mask;
        const scaled = scaleBounds(w, h, m.w, m.h, 'cover');
        setTemplateBlob(this.userId, this.selected, src, scaled.w, scaled.h);
        this.$emit('update-blob', this.selectedPageI, this.selected);
      } else {
        this.insertUploadedVideo(src, w, h);
      }
    },

    insertImage(src, w, h, isTexture) {
      const s = isTexture ? 1 : getImgScale(w, h);
      const img = { src, frame: this.mode == 'design' ? null : 'paper' };
      const blob = this.buildCenteredBlob(w * s, h * s, 'IMG', { img });
      if (isTexture)
        this.$set(blob, 'fixed_position', true);
      this.insertBlob(blob);
    },

    insertFromGiphy(gif) {
      const w = gif.w / this.pageRect.scale;
      const h = gif.h / this.pageRect.scale;
      const scale = getImgScale(w, h);
      const img = { src: gif.url, frame: null };
      const blob = this.buildCenteredBlob(w * scale, h * scale, 'IMG', { img });
      this.insertBlob(blob);
    },

    insertGiftcard(giftcard) {
      const blob = this.buildCenteredBlob(337, 212.5, 'GIFTCARD', { giftcard });
      // Gift cards are exactly the same size - if we add multiple without moving the original, it's very hard to tell
      // what's going on, so we offset successive gift cards.
      const existing = this.selectedPage.blobs.filter(x => x.type == 'GIFTCARD');
      while (existing.find(e => (blob.x == e.x) && (blob.y == e.y))) {
        blob.x += 50;
        blob.y += 50;
      }
      this.insertBlob(blob);
    },

    insertBtn(btn) {
      const blob = this.buildCenteredBlob(0, 0, 'BTN', { btn });
      this.insertBlob(blob);
    },

    insertGradient() {
      const gradient = { start: '#000000FF', stop: '#00000000', r: 0 };
      const blob = this.buildCenteredBlob(600, 840, 'GRADIENT', { gradient });
      this.insertBlob(blob);
    },

    insertTemplateBlob_IMG() {
      const src = '/template-placeholder.png';
      const blob = this.buildCenteredBlob(300, 300, 'IMG', { img: { src }, template: 'cover' });
      blob.mask = { x: blob.x, y: blob.y, w: blob.w, h: blob.h, r: 0, placeholder: src };
      this.insertBlob(blob);
    },

    insertTemplateBlob_VIDEO() {
      const src = '/template-placeholder.png';
      const blob = this.buildCenteredBlob(300, 300, 'VIDEO', { video: { src, mirror: false }, template: 'cover' });
      blob.mask = { x: blob.x, y: blob.y, w: blob.w, h: blob.h, r: 0, placeholder: src };
      this.insertBlob(blob);
    },

    toggleFitToPage() {
      const b = this.selected;
      b.r = 0;
      b.x = W / 2;
      b.y = H / 2;
      if (b.fixed_position) {
        const wPct = b.crop?.left || 0;
        const hPct = b.crop?.top || 0;
        const w1 = b.w + wPct * b.w / 50;
        const h1 = b.h + hPct * b.h / 50;
        const scale = 0.8 * ((w1 / h1 < b.w / b.h) ? b.h / h1 : b.w / w1);
        this.$delete(b, 'fixed_position');
        b.w = scale * w1;
        b.h = scale * h1;
        this.$delete(b, 'crop');
      } else {
        if (b.crop) {
          // Unset any existing crop first
          b.w = b.w * (1 + b.crop.left / 100 + b.crop.right / 100);
          b.h = b.h * (1 + b.crop.top / 100 + b.crop.bottom / 100);
          this.$delete(b, 'crop');
        }
        const { MAX_W, MAX_H } = constraints.blob[b.type];
        const scale = (b.w / b.h < MAX_W / MAX_H) ? MAX_W / b.w : MAX_H / b.h;
        const w1 = scale * b.w;
        const h1 = scale * b.h;
        this.$set(b, 'fixed_position', true);
        b.w = MAX_W;
        b.h = MAX_H;
        if (b.type != 'TEXT') {
          // We need to preserve the aspect ratio by setting a 'crop'
          const wPct = 50 * (w1 - b.w) / b.w;
          const hPct = 50 * (h1 - b.h) / b.h;
          this.$set(b, 'crop', { top: hPct, right: wPct, bottom: hPct, left: wPct, r: 0 });
        }
        if (this.selectedOptions.frame)
          this.selectedOptions.frame = null;
      }
    },

    promptUpload(type, callback) {
      // type: image | video
      // Triggers the browser to show the file upload dialog
      const el = this.$refs[`upload-${type}-input`];
      if (!el)
        return;

      const onChange = e => {
        if (e.target.files[0])
          callback(e.target.files[0]);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.onchange = onChange;
      el.click();
    },

    async transcodeVideo(file) {
      this.transcodeT = 0;
      const url = await this.$call('transcode-video', { file });
      while (true) {
        const promise = new Promise(r => this.transcodeTimeout = setTimeout(r, 1000));
        this.transcodeT += 1;
        if (this.transcodeT % 5 == 0) {
          const response = await fetch(url, { method: 'HEAD' });
          if (response.status != 404)
            break;
        }
        if (this.transcodeT >= 100)
          throw new VisibleError('Failed to convert this video file');
        await promise;
      }
      return [ url.substr(0, url.length - 4), await getMetadata(url) ];
    },

    insertUploadedVideo(src, w, h) {
      const aspectRatio = w / h;
      w = constrain(w, constraints.blob.VIDEO.MIN_W, W);
      h = constrain(h, constraints.blob.VIDEO.MIN_H, H);
      if (w / h < aspectRatio)
        h = w / aspectRatio;
      else
        w = h * aspectRatio;

      const video = { src, mirror: false, frame: 'paper' };
      const blob = this.buildCenteredBlob(w, h, 'VIDEO', { video });
      this.insertBlob(blob);
    },

    async uploadVideo(toTarget, file) {
      if (file.size / 1024 / 1024 > 50)
        throw new VisibleError('Video is too large (50 MB limit)');
      let src = URL.createObjectURL(file);
      let metadata;
      try {
        metadata = await getMetadata(src);
      } catch (e) {
        // This video format cannot be played back by the browser. We assume that we will be able to transcode any
        // video that CAN be played back (this assumption might be flawed - watch out for transcoding errors). This
        // assumption makes for a better user experience, as they don't see any delay in uploading the video file
        // before we should it in the blob. But in this case, there's no way around it, we have to wait for the
        // video to be transcoded before we create the blob. We show a progress dialog while transcoding, and will
        // complete the blob creation process on success.
        [ src, metadata ] = await this.transcodeVideo(file);
      }
      const { w, h } = metadata;
      switch (toTarget) {
        case 'new-blob':
          this.insertUploadedVideo(src, w, h);
          return;
        case 'selected-template':
          const m = this.selected.mask;
          const scaled = scaleBounds(w, h, m.w, m.h, 'cover');
          setTemplateBlob(this.userId, this.selected, src, scaled.w, scaled.h);
          this.$emit('update-blob', this.selectedPageI, this.selected);
          return;
      }
    },

    async uploadImage(toTarget, rawFile) {
      const { url, w, h } = await processImageFile(rawFile);
      this.card._newImages = this.card._newImages || new Set();
      this.card._newImages.add(url);
      switch (toTarget) {
        case 'new-blob':
          this.insertImage(url, w, h);
          return;
        case 'design-template':
          const blob = this.buildCenteredBlob(w, h, 'IMG', { img: { src: url, frame: null }, template: 'cover' });
          blob.mask = { x: blob.x, y: blob.y, w, h, r: 0, placeholder: url };
          this.insertBlob(blob);
          return;
        case 'selected-template':
          const m = this.selected.mask;
          const scaled = scaleBounds(w, h, m.w, m.h, 'cover');
          setTemplateBlob(this.userId, this.selected, url, scaled.w, scaled.h);
          this.$emit('update-blob', this.selectedPageI, this.selected);
          return;
        case 'background':
          this.card.background.img = url;
          return;
      }
    },

    uploadImageFromBtn(f) {
      for (const blob of this.selectedPage.blobs) {
        if ((blob.type == 'IMG') && blob.mask && (blob.mask.placeholder == blob.img.src)) {
          this.selected = blob;
          break;
        }
      }
      this.uploadImage(this.selected?.template ? 'selected-template' : 'new-blob', f);
    },

    isActive() {
      return document.activeElement.contains(this.$el) || this.$el.contains(document.activeElement);
    },

    hasDropZones(page) {
      return !!page.blobs.find(x => x.template && x.type == 'IMG');
    },

    isPrimary(blob) {
      return (blob.user_id == this.userId) || (blob.template && !blob.user_id);
    },

    isBlobVisible(b, mode) {
      return isBlobVisible(b, mode || this.mode, this.card.design_id);
    },

    onPageClick(e, pageI) {
      this.didInteract = true;
      if (this.isTransformInProgress || this.fontAlignmentMenu) {
        // Note: sometimes you get into a situation where you can't click on anything. I think that's because there was
        // something lingering that was incorrectly setting 'isTransformInProgress'. I changed how that property is
        // computed, but I'm not sure it will address the issue - it might also have something to do with the menus. I
        // can't reliably reproduce it at the moment, but the following may help you debug in the future:
        // console.log(JSON.stringify({
        //   isTransformInProgress: this.isTransformInProgress,
        //   fontAlignmentMenu: this.fontAlignmentMenu,
        //   frameMenu: this.frameMenu
        // }, null, 2));
        return;
      }
      if (this.selectedPageI != pageI) {
        this.swipe(pageI - this.selectedPageI);
        return;
      }
      if (this.selected) {
        this.selected = null;
        return;
      }
      if (this.isPageReadonly)
        return;
      if (this.coverOnly)
        // This is perhaps the wrong place to check this, but there was a glitch in the img-to-design tool where
        // clicking down on the button, then dragging over the empty template, then releasing would trigger a page
        // click and insert a text blob
        return;
      const rect = this.$refs['page-container'][0].getBoundingClientRect();
      this.insertText((e.clientX - rect.x) / this.pageRect.scale, (e.clientY - rect.y) / this.pageRect.scale);
    },

    onClickOutside() {
      if (this.ignoreInput)
        return;
      this.selected = null;
    },

    async swipe(d, blur=true) {
      const newPage = this.selectedPageI + d;
      if (d == 0 || newPage < 0 || newPage >= this.pages.length)
        return;
      this.didInteract = true;
      this.isSwiping = true;
      this.selected = null;
      this.selectedPageI = newPage;

      if (blur)
        // This makes sure the keyboard hides on mobile, but it also causes problems if we have multiple event
        // handlers attached to the body, as we do when the purchase dialog is open over the featured page
        document.activeElement.blur();

      // Make sure this delay matches the CSS 'page-transition-container' transition duration
      await new Promise(r => setTimeout(r, 200));

      this.isSwiping = false;
      this.$forceUpdate();
    },

    addPage(i, layout) {
      if (!layout)
        return null;

      const blobs = layout.blobs.map(x => ({ ..._.cloneDeep(x), id: uuid4() }));
      const page = { id: uuid4(), blobs };
      if (this.mode == 'design')
        page.design = true;
      this.card.pages.splice(i + 1, 0, page);
      for (const blob of blobs)
        this.$emit('insert-blob', i + 1, blob);
      this.$emit('insert-page', page.id);
      this.$emit('input', this.card);
      if (!this.fixedLastPage)
        this.swipe(i + 1 - this.selectedPageI);

      this.showAddNewPage = false;
      this.showPageOptions = false;
      return page;
    },

    changePageGridRow(d) {
      if (this.viewAs != 'grid')
        return;
      const cols = Math.max(3, ~~((this.$refs.draggable.clientWidth - 24) / 200));
      this.selectedPageI = Math.max(0, Math.min(this.pages.length - 1, this.selectedPageI + d * cols));
    },

    onKeyDown(e) {
      if (!this.isActive() || (this.ignoreInput && !this.isCropping))
        return;

      switch (e.keyCode) {
        case 8: // Backspace
        case 46: // Delete
          if ((this.selectedI != -1) && this.canEditSelected && (![ 'GIFTCARD', 'GROUP_VIDEO' ].includes(this.selected.type) || this.mode == 'purchase' || this.selected.sample) && !this.selected.btn?.form)
            this.tryDeleteBlob(this.selectedPageI, this.selectedI);
          else if ((this.viewAs == 'grid') && this.canDeleteSelectedPage)
            this.tryDeletePage(this.selectedPageI);
          return;
        case 27: // Escape
          if (this.isCropping)
            this.isCropping = false;
          else if (this.selected)
            this.selected = null;
          return;
        case 9: // Tab
          const blobs = this.selectedPage.blobs;
          if ((this.mode == 'design') && blobs.length) {
            if (this.selected)
              this.selected = blobs[ (blobs.length + this.selectedI + (e.shiftKey ? -1 : 1)) % blobs.length ];
            else
              this.selected = blobs[0];
          }
          e.preventDefault();
          return;
        case 37: // Left arrow
          this.swipe(-1, false);
          return;
        case 39: // Right arrow
          this.swipe(1, false);
          return;
        case 13: // Return
        case 32: // Space bar
          if (this.isCropping)
            this.isCropping = false;
          else if (this.viewAs == 'grid')
            this.viewAs = null;
          break;
        case 38: // Up arrow
          if (e.shiftKey)
            this.viewAs = this.views[ (((this.views.indexOf(this.viewAs) - 1) % this.views.length) + this.views.length) % this.views.length ];
          else
            this.changePageGridRow(-1);
          return;
        case 40: // Down arrow
          if (e.shiftKey)
            this.viewAs = this.views[ (this.views.indexOf(this.viewAs) + 1) % this.views.length ];
          else
            this.changePageGridRow(1);
          return;
      }
    },

    onKeyDownCapture(e) {
      if (e.keyCode == 17)
        this.ctrlDown = true;
    },

    onKeyUp(e) {
      if (e.keyCode == 17)
        this.ctrlDown = false;
    },

    onGestureStart(e) {
      if (!this.canTransformSelected)
        return;
      handleGesture(
        e,
        (distance, angle) => {
          this.isRootTransformInProgress = true;
          this.gestureStart = {
            r: angle - this.selected.r,
            d: distance,
            w0: this.selected.w,
            h0: this.selected.h
          };
        }
      );
    },
    onGestureMove(e) {
      if (!this.canTransformSelected || isZoomed())
        return;
      handleGesture(
        e,
        (distance, angle) => {
          if (!this.gestureStart)
            // It's possible that this hasn't been initialized yet if we added a touch with one already down
            this.onGestureStart(e);
          const s = this.gestureStart;
          const r = angle - s.r;
          this.selected.r = this.mode == 'design' || this.selected.mask ? r : constraints.roundDegrees(r);
          const type = this.selected.type;
          if (type == 'TEXT') {
            // Scale just the width
            const scale = distance / s.d;
            this.selected.w = constrain(s.w0 * scale, constraints.blob.TEXT.MIN_W, W);
          } else {
            // Preserve aspect ratio when scaling anything else
            const min = Math.max(constraints.blob[type].MIN_W / s.w0, constraints.blob[type].MIN_H / s.h0);
            const max = Math.max(W / s.w0, H / s.h0);
            const scale = this.selected.mask ? Math.max(distance / s.d, min) : constrain(distance / s.d, min, max);
            this.selected.w = s.w0 * scale;
            this.selected.h = s.h0 * scale;
          }
        }
      );
    },
    onGestureEnd(e) {
      if (!this.gestureStart)
        return;
      // Don't let the swipe gesture fire for a bit to make sure we have time to lift both fingers off the screen
      setTimeout(() => this.isRootTransformInProgress = false, SWIPE_AFTER_GESTURE_DELAY_MS);
      e.stopPropagation();
      this.gestureStart = null;
    },

    onGlobalTouchMove(e) {
      // This 'touchmove' handler is attached to the root and prevents pull-down-to-refresh in mobile Chrome. Note that
      // we explicitly check to see if we have a focused text area (as in a TEXT blob), as we do NOT want to interfere
      // with the default event handling when editing text.
      if (this.selected && (!document.activeElement || document.activeElement.contentEditable != 'true'))
        e.preventDefault();
    },

    async onBlobContextMenu(e) {
      if (this.isCropping)
        return;
      this.contextMenu = null;
      await this.$nextTick();
      this.contextMenu = { x: e.clientX, y: e.clientY };
    },

    async onResize() {
      await this.$nextTick();
      const el = this.$refs['pages-container'];
      if (!el)
        return;
      const s = getComputedStyle(el);
      const pw = parseInt(s.getPropertyValue('padding-left')) + parseInt(s.getPropertyValue('padding-right'));
      const ph = parseInt(s.getPropertyValue('padding-top')) + parseInt(s.getPropertyValue('padding-bottom'));
      let w = el.clientWidth - pw;
      let h = el.clientHeight - ph;
      if (w / h > W / H) {
        w = h * W / H;
      } else {
        h = w * H / W;
      }
      this.disablePageTransition = true;
      await this.$nextTick();
      this.pageRect.scale = w / W; // Same as h / H
      this.pageRect.w = w;
      this.pageRect.h = h;
      await this.$nextTick();
      this.disablePageTransition = false;
      this.$emit('resize', this.pageRect)
    },

    async loadSelectedSignature() {
      if (!this.selected || !this.orderId || (this.card.user_id != this.userId))
        return;
      this.selectedSignature = {};
      const params = { order_id: this.orderId, user_id: this.selected.user_id };
      this.selectedSignature = await this.$call('get-author-email', params);
    },

    loadedCoverBlob(blobId) {
      this.coverBlobIdsToLoad.delete(blobId);
      if (this.coverBlobIdsToLoad.size == 0 && !this.didLoadCover) {
        this.didLoadCover = true;
        this.$emit('loaded-cover');
      }
    },

    fixScroll() {
      // We've already hidden the overflow such that this should not be necesary, but typing in a contenteditable div
      // seems to force the component to scroll (at least in Chrome). This can happen if a user is typing in a text
      // blob and it expands below the bottom of the page. This fix will cause a bit of a "flash" as things reposition,
      // but that's far better than letting users think they've entered legible text that no one else can see.
      setTimeout(() => this.$el.scrollTop = 0);
    },

    isValidPage(x) {
      return !!x && Number.isInteger(parseFloat(x)) && (x > 0 && x <= this.pages.length);
    },

    onPageInput(x) {
      if (this.isValidPage(x))
        this.selectedPageI = x - 1;
    },

    swapDesignBlobs(p1, p2) {
      const [ p1_design, p1_remaining ] = _.partition(p1.blobs, x => x.for_design_id);
      const [ p2_design, p2_remaining ] = _.partition(p2.blobs, x => x.for_design_id);
      p1.blobs = [ ...p2_design, ...p1_remaining ];
      p2.blobs = [ ...p1_design, ...p2_remaining ];
    },

    async onMoveEnd(target) {
      const d = this.changeBlobPageD;
      if (!this.selected)
        return;
      if (!d && (target?.id == this.selected.id))
        // We're just dragging a blob around on the same page
        return;

      // If we're dealing with a masked image template, extract the blob as a new one
      if (this.selected.mask) {
        if (this.selectedOptions.src == this.selected.mask.placeholder)
          // No dragging around the placeholder image
          return;
        const blob = {
          id: uuid4(),
          ..._.cloneDeep(_.omit(this.selected, [ 'id', 'template', 'mask' ]))
        };
        // When an image is dragged outside a blob, it must abide by the max constraints
        const scale = Math.min(constraints.blob.IMG.MAX_W / blob.w, constraints.blob.IMG.MAX_H / blob.h);
        if (scale < 1) {
          blob.w *= scale;
          blob.h *= scale;
        }
        resetTemplateBlob(this.selected);
        this.$emit('update-blob', this.selectedPageI, this.selected);
        this.insertBlob(blob, false);
        this.selected = blob;
        await this.$nextTick();
        return this.onMoveEnd(target);
      }

      // At this point, we know we are dealing with just a regular blob

      if (target && (this.selected.type == target.type) && target.mask && !d) {
        // We're moving this image blob into an image template mask
        const b = this.selected;
        if (b.crop) {
          b.w = b.w * (1 + b.crop.left / 100 + b.crop.right / 100);
          b.h = b.h * (1 + b.crop.top / 100 + b.crop.bottom / 100);
          this.$delete(b, 'crop');
        }
        const m = target.mask;
        const scaled = scaleBounds(b.w, b.h, m.w, m.h, 'cover');
        setTemplateBlob(b.user_id, target, this.selectedOptions.src, scaled.w, scaled.h);
        this.selectedPage.blobs = this.selectedPage.blobs.filter(x => x.id != b.id);
        this.$emit('delete-blob', b, this.selectedPageI);
        this.$emit('update-blob', this.selectedPageI, target);
        this.selected = target;
        return;
      }

      if (!d)
        return;
      if (this.selectedPageI + d < 0)
        return;
      await this.updateSelectedBlobPage(d, true);
    },

    async updateSelectedBlobPage(d, fromDrag=false) {
      if (this.selectedPageI + d == this.card.pages.length) {
        const page = { id: uuid4(), blobs: [] };
        if (this.mode == 'design')
          page.design = true;
        this.card.pages.push(page);
      }

      const blob = this.selected;
      this.selected = null;
      await this.$nextTick();
      this.selectedPage.blobs = this.selectedPage.blobs.filter(x => x.id != blob.id);

      this.swipe(d, false);

      if (fromDrag)
        // It looks weird to have the blob all the way against the edge of the destination page. Note that this magic
        // number is derived empirically based on the 'getScale' function.
        blob.x = 300 + d * 60;

      this.insertBlob(blob, true);
      await this.$nextTick();
      this.selected = blob;
      this.$emit('save');
    },

    async updateSelectedBlobLayer(d) {
      const i = this.selectedI;
      if (!this.selectedPage.blobs[i + d])
        return;
      const blob = this.selected;
      this.selected = null;
      this.selectedPage.blobs[i] = this.selectedPage.blobs[i + d];
      this.selectedPage.blobs[i + d] = blob;
      this.selected = blob;
      this.$emit('input', this.card);
      this.$emit('update-layers', this.selectedPage.id);
    },

    duplicateBlob(blob, strip) {
      // When duplicating blobs in design mode
      const copy = _.cloneDeep(blob);
      copy.id = uuid4();
      const toUnset = [ 'for_design_id', 'fixed_position', ...(this.mode == 'design' ? [] : [ 'template', 'mask', 'text.event_link' ]) ];
      for (const key of toUnset)
        _.unset(copy, key);
      return copy;
    },

    async duplicateSelectedBlob() {
      const strip = [ 'for_design_id', ...(this.mode == 'design' ? [] : [ 'template', 'mask', 'text.event_link' ]) ];
      const blob = this.duplicateBlob(this.selected, strip);
      blob.x = blob.x + (blob.x > W - 100 ? -30 : 30);
      blob.y = blob.y + (blob.y > H - 100 ? -30 : 30);
      this.insertBlob(blob);
      await new Promise(setTimeout);
      this.selected = blob;
      this.$emit('insert-blob', this.selectedPageI, blob, false);
      shouldSaveBlob(blob) && this.$emit('input', this.card);
    },

    duplicateCurrentPage() {
      const blobs = this.selectedPage.blobs.filter(
        x => [ 'TEXT', 'IMG', 'VIDEO' ].includes(x.type) && (!x.for_design_id || (x.for_design_id == this.card.design_id))
      ).map(
        x => ({
          ..._.cloneDeep(x),
          id: uuid4(),
          user_id: this.userId,
          for_design_id: undefined,
          template: x.template == 'cover' ? 'page' : x.template
        })
      );
      const page = { id: uuid4(), blobs };
      if (this.mode == 'design')
        page.design = true;
      const atIndex = this.isOwner ? (this.selectedPageI + 1) : this.pages.length;
      this.card.pages.splice(atIndex, 0, page);
      for (const blob of _.cloneDeep(this.selectedPage.blobs)) {
        if (blob.type == 'GROUP_VIDEO')
          continue;
        if (blob.template == 'cover')
          blob.template = 'page';
        this.$emit('insert-blob', atIndex, { ...blob, id: uuid4() });
      }
      this.$emit('insert-page', page.id);
      this.$emit('input', this.card);
      this.swipe(atIndex - this.selectedPageI);

      this.showPageOptions = false;
    },

    closeGrid(i) {
      this.viewAs = null;
      if (i != -1)
        this.swipe(i - this.selectedPageI);
    },

    selectLayer(layer) {
      this.viewAs = null;
      if (!layer.editable)
        return;
      const blob = this.selectedPage.blobs.find(x => x.id == layer.blobs[0].id);
      if (isBlobEditable(blob, this.mode, this.userId, this.isOwner))
        setTimeout(() => this.selected = blob);
    },

    updateLayers() {
      // Called after we've dragged layers around
      const blobs = [];
      for (const layer of this.selectedPageLayers)
        for (const blob of layer.blobs)
          blobs.push(blob);
      blobs.reverse();
      this.selectedPage.blobs = blobs;
      this.$emit('update-layers', this.selectedPage.id);
      this.$emit('input', this.card);
      this.$emit('save');
    },

    isEffectActive(effect) {
      if ((effect.name == 'None') && !this.activeEffectNames.size)
        return true;
      return this.activeEffectNames.has(effect.name);
    },

    async toggleEffect(effect) {
      const existing = this.selected.text.css || {};
      const css = _.pick(existing, TEXT_EFFECTS_PRESERVE_KEYS);
      let names = new Set(this.activeEffectNames);

      // Make sure that we clear everything first
      this.$set(this.selected.text, 'css', { ...css });

      if (!effect.css)
        // Setting to 'None'
        return;

      if (names.has(effect.name)) {
        names.delete(effect.name);
      } else {
        if (!TEXT_EFFECTS_COMBINE.includes(effect.name)) {
          const newNames = new Set();
          for (const name of TEXT_EFFECTS_COMBINE)
            if (names.has(name))
              newNames.add(name);
          names = newNames;
        }
        names.add(effect.name);
      }

      // 'names' now contains the set of all the effects to apply
      for (const effect of TEXT_EFFECTS) {
        if (names.has(effect.name)) {
          Object.assign(css, effect.css);
          for (const name of names)
            if (effect.combine && effect.combine[name])
              Object.assign(css, effect.combine[name]);
        }
      }

      // We need to make sure clearing all the effects has taken
      await this.$nextTick();
      this.$set(this.selected.text, 'css', css);
    },

    toggleCrop() {
      this.isCropping = !this.isCropping;
      if (!this.isCropping)
        return;
      if (!this.selected.crop)
        this.$set(this.selected, 'crop', { top: 0, right: 0, bottom: 0, left: 0, r: 0 });
    }
  },

  watch: {
    pages: {
      immediate: true,
      async handler(v) {
        this.didLoadCover = false;
        if (v.length == 0) {
          this.coverBlobIdsToLoad = new Set();
          this.didLoadCover = true;
          this.$emit('loaded-cover');
        } else {
          const blobs = v.length > 0 ? v[0].blobs : [];
          this.coverBlobIdsToLoad = new Set( blobs.map(x => x.id) );
        }
        if ([ 'purchase', 'edit' ].includes(this.mode) || v.length == 0)
          this.adjustPages();

        this.$emit('update:show-buttons', false);
        await this.$nextTick();
        this.$emit('update:show-buttons', true);
      }
    },

    'card.background': {
      deep: true,
      handler() {
        this.$emit('input', this.card);
      }
    },

    averageBackgroundColorPromise: {
      immediate: true,
      async handler(v) {
        this.averageBackgroundColor = await v;
      }
    },

    'card.skin.card_fixed_last_page': {
      immediate: true,
      handler(v) {
        if (!v) {
          this.fixedLastPage = null;
          return;
        }

        // For legacy reasons, this value can be either a UUID pointing to a card design, or a serialized page
        try {
          if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v))
            this.getPublicDesign(v).then(design => this.fixedLastPage = stripForDesignId(_.cloneDeep(design.pages[0])));
          else
            this.fixedLastPage = stripForDesignId(JSON.parse(v));
        } catch (e) {
          console.error('Error retrieving fixed last page');
          this.fixedLastPage = null;
        }
      }
    },

    selected: {
      deep: true,
      handler(selected, prev) {
        if ((selected !== prev) && prev) {
          getSelection().removeAllRanges();
          this.fontAlignmentMenu = false;
          this.showSmartText = false;
          this.contextMenu = null;
        }
        if (selected && (selected.type != 'TEXT') && (this.mode != 'design') && !this.contextMenu)
          document.activeElement.blur();
        if (!selected)
          this.isCropping = false;
        this.$emit('select-blob', selected);
      },
    },

    mode(v) {
      if (v == 'view')
        this.selected = null;
    },

    contextMenu(v) {
      if (v && this.isOwner && !this.isPrimary(this.selected))
        this.loadSelectedSignature();
    },

    selectedPageI(x) {
      this.pageInput = x + 1;
      if (this.viewAs == 'grid') {
        this.$refs['grid-page'][x].scrollIntoView({ block: 'nearest' });
        this.$refs['grid-page'][x].focus();
      }
    },

    selectedPage(v) {
      this.guide = { horizontal: -1, vertical: -1 };
      if (!v || ![ 'edit', 'view' ].includes(this.mode) || !this.selectedPageI)
        return;
      for (const blob of v.blobs) {
        if (blob.type == 'GROUP_VIDEO') {
          this.$emit('page-group-video', blob);
          break;
        }
      }
    },

    selectedPageLayersCondition: {
      immediate: true,
      deep: true,
      handler() {
        if (this.viewAs != 'layers')
          return;
        this.selected = null;
        const layers = [ { blobs: [], rearrange: false, editable: false, visible: true } ];
        if (this.selectedPage) {
          // We want to collapse adjacent template blobs together
          // Blobs that are part of a design (null user ID) or template cannot be rearranged
          for (const blob of this.selectedPage.blobs) {
            if (this.mode == 'design') {
              layers.push({ blobs: [ blob ], rearrange: true, editable: true, visible: true });
              continue;
            }
            if (blob.for_design_id && (blob.for_design_id != this.card.design_id))
              continue;
            if (!blob.user_id && !blob.template) {
              // This blob is part of the design
              if (!layers[ layers.length - 1 ].rearrange && !layers[ layers.length - 1 ].editable)
                layers[ layers.length - 1 ].blobs.push(blob);
              else
                layers.push({ blobs: [ blob ], rearrange: false, editable: false, visible: true });
            } else if (blob.type == 'BTN' && blob.btn.form) {
              layers.push({ blobs: [ blob ], rearrange: false, editable: false, visible: false });
            } else if (blob.template) {
              layers.push({ blobs: [ blob ], rearrange: false, editable: true, visible: true });
            } else {
              layers.push({ blobs: [ blob ], rearrange: true, editable: true, visible: true });
            }
          }
          layers.reverse();
        }
        this.selectedPageLayers = layers;
      }
    },

    showEditBtn(v) {
      setTimeout(() => this.isRootTransformInProgress = !!v);
    },

    async viewAs(v) {
      if (v == 'grid') {
        this.selected = null;
        await this.$nextTick();
        this.$refs['grid-page'][this.selectedPageI].scrollIntoView({ behavior: 'instant', block: 'nearest' });
      }
      this.$emit('view-as', v);
    },

    async fontAlignmentMenu(v) {
      this.selectedTextCSSDefaults = { 'line-height': 1.5, 'letter-spacing': '0em' };
      if (v && this.selected.for_design_id) {
        const design = await this.getPublicDesign(this.selected.for_design_id);
        const blob = _.flatten(design.pages.map(x => x.blobs)).find(x => x.id == this.selected.id);
        if (blob)
          Object.assign(this.selectedTextCSSDefaults, blob.text.css);
      }
    }
  }
}
</script>
